import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, ChevronRight, Lightbulb, ArrowRight } from 'lucide-react';

// Home page theme colors
const PRIMARY = '#2563EB';
const PRIMARY_HOVER = '#1D4ED8';
const PRIMARY_LIGHT = '#EAF2FF';
const BG = '#F8FAFC';
const CARD_BG = '#FFFFFF';
const TEXT_DARK = '#1E293B';
const TEXT_MUTED = '#64748B';
const BORDER = '#E5E7EB';
// Sidebar menu typography
const MENU_TITLE_FONT = '"Poppins", "Inter", sans-serif';
const MENU_SECTION_FONT = '"Poppins", "Inter", sans-serif';
const SUBMENU_FONT = '"Inter", "Segoe UI", sans-serif';

type MenuItem = { id: string; label: string; children?: { id: string; label: string }[] };
type MenuSection = {
  id: string;
  label: string;
  children: MenuItem[];
};

const SIDEBAR_SECTIONS: MenuSection[] = [
  {
    id: 'fundamentals',
    label: 'Python Fundamentals',
    children: [
      {
        id: 'variables-datatypes',
        label: 'Variables & Data Types',
        children: [
          { id: 'number', label: 'Number' },
          { id: 'string', label: 'String' },
          { id: 'tuple', label: 'Tuple' },
          { id: 'list', label: 'List' },
          { id: 'dictionary', label: 'Dictionary' },
          { id: 'sets', label: 'Sets' },
        ],
      },
      {
        id: 'conditions-loops',
        label: 'Conditions & Loops',
        children: [
          { id: 'conditional-statements', label: 'Conditional Statements' },
          { id: 'python-loops', label: 'Python Loops' },
        ],
      },
      { id: 'functions', label: 'Functions' },
      { id: 'python-lambda', label: 'Lambda Function' },
      { id: 'file-handling', label: 'File Handling' },
      { id: 'iterators', label: 'Iterators' },
      { id: 'generators', label: 'Generators' },
      { id: 'try-except-finally', label: 'Try Exception Finally' },
      { id: 'python-json', label: 'Python JSON' },
      {
        id: 'oop',
        label: 'OOP (Classes & Objects)',
        children: [
          { id: 'oop-class', label: 'Class' },
          { id: 'oop-constructor', label: 'Constructor' },
          { id: 'oop-inheritance', label: 'Inheritance' },
          { id: 'oop-polymorphism', label: 'Polymorphism' },
          { id: 'oop-abstraction', label: 'Abstraction' },
          { id: 'oop-encapsulation', label: 'Encapsulation' },
          { id: 'oop-abstract-class', label: 'Abstract Class' },
        ],
      },
    ],
  },
  {
    id: 'advanced',
    label: 'Advanced Python',
    children: [
      { id: 'comprehensions', label: 'Comprehensions' },
      { id: 'exception-handling', label: 'Exception Handling' },
      { id: 'multithreading', label: 'Multithreading' },
      { id: 'multiprocessing', label: 'Multiprocessing' },
    ],
  },
  {
    id: 'data-libraries',
    label: 'Data & Big Data Libraries',
    children: [
      { id: 'numpy', label: 'NumPy' },
      { id: 'pandas', label: 'Pandas' },
      { id: 'pyspark', label: 'PySpark' },
    ],
  },
  {
    id: 'web-frameworks',
    label: 'Web & API Frameworks',
    children: [
      { id: 'django', label: 'Django' },
      { id: 'drf', label: 'Django REST Framework' },
      { id: 'fastapi', label: 'FastAPI' },
      { id: 'flask', label: 'Flask' },
    ],
  },
  {
    id: 'career-paths',
    label: 'Python Career Paths',
    children: [
      { id: 'backend-developer', label: 'Backend Developer' },
      { id: 'data-engineer', label: 'Data Engineer' },
      { id: 'data-analyst', label: 'Data Analyst' },
      { id: 'data-scientist', label: 'Data Scientist' },
    ],
  },
];

type SectionLandingContent = {
  title: string;
  intro: string;
  whatYouWillLearn: string[];
  whyItMatters: string;
  actionPrompt: string;
  startWithTopicId: string | null; // topic/section id for the CTA button, or null to hide
  startWithLabel: string;
  startWithButtonText?: string; // optional custom button text (e.g. "Next -> Variables & Datatype")
  tip: string;
};

const SECTION_LANDING: Record<string, SectionLandingContent> = {
  fundamentals: {
    title: 'Python Fundamentals',
    intro:
      'Welcome to Python Fundamentals, the foundation of your Python journey. In this section, you will learn the core programming concepts required to build real-world applications, automation scripts, data workflows, and backend systems.',
    whatYouWillLearn: [
      'Variables and Data Types',
      'Conditional Statements and Logic Building',
      'Loops for automation and iteration',
      'Functions for reusable code',
      'File Handling for reading and writing data',
      'Object-Oriented Programming (Classes & Objects)',
    ],
    whyItMatters:
      'Strong fundamentals make advanced topics like Data Engineering, Backend Development, and AI Engineering easier to understand and apply in real projects.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'variables-datatypes',
    startWithLabel: 'Variables & Data Types',
    startWithButtonText: 'Next -> Variables & Datatype',
    tip: 'Complete all modules in this section before moving to Advanced Python to ensure a smooth learning experience.',
  },
  advanced: {
    title: 'Advanced Python',
    intro:
      'Build on your fundamentals with advanced Python concepts: comprehensions, robust error handling, and concurrency with threads and processes.',
    whatYouWillLearn: [
      'Comprehensions',
      'Exception Handling',
      'Multithreading',
      'Multiprocessing',
    ],
    whyItMatters:
      'These skills are essential for writing production-ready, performant Python used in backend services and data pipelines.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'comprehensions',
    startWithLabel: 'Comprehensions',
    tip: 'Finish Python Fundamentals before starting this section for the best experience.',
  },
  'data-libraries': {
    title: 'Data & Big Data Libraries',
    intro:
      'Work with data at scale using NumPy, Pandas, and PySpark for analysis, transformation, and big data processing.',
    whatYouWillLearn: ['NumPy', 'Pandas', 'PySpark'],
    whyItMatters:
      'Data libraries are the backbone of data engineering and data science roles.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'numpy',
    startWithLabel: 'NumPy',
    tip: 'Practice with real datasets to build intuition for each library.',
  },
  'web-frameworks': {
    title: 'Web & API Frameworks',
    intro:
      'Build web applications and REST APIs with Django, Django REST Framework, FastAPI, and Flask.',
    whatYouWillLearn: ['Django', 'Django REST Framework', 'FastAPI', 'Flask'],
    whyItMatters:
      'Web frameworks are core to Backend Developer and full-stack roles.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'django',
    startWithLabel: 'Django',
    tip: 'Start with one framework and build a small project before exploring others.',
  },
  'career-paths': {
    title: 'Python Career Paths',
    intro:
      'Explore roles where Python shines: Backend Developer, Data Engineer, Data Analyst, and Data Scientist.',
    whatYouWillLearn: [
      'Backend Developer',
      'Data Engineer',
      'Data Analyst',
      'Data Scientist',
    ],
    whyItMatters:
      'Understanding these paths helps you choose a direction and tailor your learning.',
    actionPrompt: 'Select a role from the left menu to explore.',
    startWithTopicId: 'backend-developer',
    startWithLabel: 'Backend Developer',
    tip: 'Many roles overlap; skills from one path often apply to another.',
  },
};

// Topic-specific content: when activeId matches, show this instead of section landing
const TOPIC_CONTENT_IDS = new Set([
  'variables-datatypes',
  'conditions-loops',
  'conditional-statements',
  'python-loops',
  'functions',
  'python-lambda',
  'file-handling',
  'iterators',
  'generators',
  'try-except-finally',
  'python-json',
  'oop',
  'oop-class',
  'oop-constructor',
  'oop-inheritance',
  'oop-polymorphism',
  'oop-abstraction',
  'oop-encapsulation',
  'oop-abstract-class',
  'number',
  'string',
  'tuple',
  'list',
  'dictionary',
  'sets',
]);

// Table of contents for right-side "On this page" navigation. depth: 0 = main, 1 = sub
type TocItem = { id: string; label: string; depth?: 0 | 1 };
const CONDITIONS_LOOPS_TOC: TocItem[] = [
  { id: 'conditional-statements', label: 'Conditional Statements', depth: 0 },
  { id: 'if-statement', label: 'if statement', depth: 1 },
  { id: 'if-else', label: 'if-else', depth: 1 },
  { id: 'nested-if', label: 'nested if', depth: 1 },
  { id: 'if-elif-else', label: 'if-elif-else ladder', depth: 1 },
  { id: 'shorthand-if-else', label: 'Short Hand if-else', depth: 1 },
  { id: 'python-loops', label: 'Python Loops', depth: 0 },
  { id: 'while-loop', label: 'While Loop', depth: 1 },
  { id: 'for-loop', label: 'For Loop', depth: 1 },
  { id: 'nested-loops', label: 'Nested Loops', depth: 1 },
];
const PAGE_TOC: Record<string, TocItem[]> = {
  'conditions-loops': CONDITIONS_LOOPS_TOC,
  'conditional-statements': CONDITIONS_LOOPS_TOC,
  'python-loops': CONDITIONS_LOOPS_TOC,
  'variables-datatypes': [
    { id: 'variable', label: 'Variable', depth: 0 },
    { id: 'rules-naming', label: 'Rules for Naming Variables', depth: 1 },
    { id: 'multiple-assignment', label: 'Multiple Variable Assignment', depth: 1 },
    { id: 'changing-type', label: 'Changing Variable Type', depth: 1 },
    { id: 'data-types', label: '2. Data Types', depth: 0 },
    { id: 'immutable-datatypes', label: 'Immutable Datatypes', depth: 1 },
    { id: 'mutable-datatypes', label: 'Mutable Datatypes', depth: 1 },
  ],
  'number': [
    { id: 'number-intro', label: 'Types of Numbers', depth: 0 },
    { id: 'integer', label: 'Integer (int)', depth: 1 },
    { id: 'float', label: 'Float (float)', depth: 1 },
    { id: 'complex', label: 'Complex Numbers', depth: 1 },
    { id: 'type-checking', label: 'Type Checking', depth: 1 },
    { id: 'type-conversion', label: 'Type Conversion', depth: 1 },
    { id: 'arithmetic', label: 'Arithmetic Operations', depth: 1 },
  ],
  'sets': [
    { id: 'sets-create', label: 'Create a Set', depth: 0 },
    { id: 'sets-read', label: 'Read or Access Set Items', depth: 0 },
    { id: 'sets-add', label: 'Add or Change', depth: 0 },
    { id: 'sets-remove', label: 'Remove Set Items', depth: 0 },
    { id: 'sets-join', label: 'Join Sets', depth: 0 },
  ],
  'dictionary': [
    { id: 'dict-create', label: 'Creating a Dictionary', depth: 0 },
    { id: 'dict-access', label: 'Access Dictionary Items', depth: 0 },
    { id: 'dict-update', label: 'Update Dictionary Items', depth: 0 },
    { id: 'dict-remove', label: 'Remove Dictionary Items', depth: 0 },
    { id: 'dict-copy', label: 'copy() Method', depth: 0 },
    { id: 'dict-fromkeys', label: 'fromkeys() Method', depth: 0 },
    { id: 'dict-setdefault', label: 'setdefault() Method', depth: 0 },
    { id: 'dict-check-key', label: 'Check if Key Exists', depth: 0 },
    { id: 'dict-comprehension', label: 'Dictionary Comprehension', depth: 0 },
  ],
  'string': [
    { id: 'string-create', label: 'Create — Ways to Define a String', depth: 0 },
    { id: 'string-read', label: 'Read — Access String (Indexing & Slicing)', depth: 0 },
    { id: 'string-update', label: 'Update — Not Allowed (Immutable)', depth: 0 },
    { id: 'string-insert', label: 'Insert — Use Concatenation Instead', depth: 0 },
    { id: 'string-delete', label: 'Delete — Entire String Only', depth: 0 },
  ],
  'tuple': [
    { id: 'tuple-create', label: 'Create', depth: 0 },
    { id: 'tuple-read', label: 'Read or Access', depth: 0 },
    { id: 'tuple-update', label: 'Update', depth: 0 },
    { id: 'tuple-delete', label: 'Delete', depth: 0 },
    { id: 'tuple-packing', label: 'Packing and Unpacking', depth: 0 },
    { id: 'tuple-methods', label: 'Tuple Methods', depth: 0 },
    { id: 'tuple-concat', label: 'Concatenation and Multiplication', depth: 0 },
    { id: 'tuple-max-min', label: 'max(), min(), sum(), any()', depth: 0 },
    { id: 'tuple-membership', label: 'Membership Operations', depth: 0 },
    { id: 'tuple-nested', label: 'Nested Tuples', depth: 0 },
  ],
  'list': [
    { id: 'list-create', label: 'Create', depth: 0 },
    { id: 'list-read', label: 'Read or Access', depth: 0 },
    { id: 'list-update', label: 'Update', depth: 0 },
    { id: 'list-insert', label: 'Insert New Elements', depth: 0 },
    { id: 'list-remove', label: 'Remove', depth: 0 },
  ],
  'functions': [
    { id: 'func-syntax', label: 'Syntax of Function', depth: 0 },
    { id: 'func-create', label: 'Creating a Function', depth: 0 },
    { id: 'func-calling', label: 'Calling a Function', depth: 0 },
    { id: 'func-parameters', label: 'Parameters', depth: 0 },
    { id: 'func-args', label: 'Arbitrary Arguments (*args)', depth: 0 },
    { id: 'func-keyword', label: 'Keyword Arguments', depth: 0 },
    { id: 'func-kwargs', label: 'Arbitrary Keyword Arguments (**kwargs)', depth: 0 },
    { id: 'func-default', label: 'Default Parameter Value', depth: 0 },
    { id: 'func-return', label: 'Return Values', depth: 0 },
    { id: 'func-docstring', label: 'Docstrings', depth: 0 },
  ],
  'python-lambda': [
    { id: 'lambda-intro', label: 'Lambda (Anonymous Function)', depth: 0 },
    { id: 'lambda-syntax', label: 'Syntax', depth: 0 },
    { id: 'lambda-single', label: 'Single Argument', depth: 0 },
    { id: 'lambda-multiple', label: 'Multiple Arguments', depth: 0 },
    { id: 'lambda-filter', label: 'filter() with Lambda', depth: 0 },
    { id: 'lambda-map', label: 'map() with Lambda', depth: 0 },
    { id: 'lambda-reduce', label: 'reduce() with Lambda', depth: 0 },
  ],
  'file-handling': [
    { id: 'file-open', label: 'open() Function & Syntax', depth: 0 },
    { id: 'file-modes', label: 'File Access Modes', depth: 0 },
    { id: 'file-create', label: 'How to Create a Text File', depth: 0 },
    { id: 'file-read', label: 'How to Read Files', depth: 0 },
    { id: 'file-close', label: 'The close() Method', depth: 0 },
    { id: 'file-write', label: 'The write() Method', depth: 0 },
    { id: 'file-position', label: 'File Positions (tell & seek)', depth: 0 },
    { id: 'file-with', label: 'Using with Statement (Best Practice)', depth: 0 },
    { id: 'file-rename', label: 'The rename() Method', depth: 0 },
    { id: 'file-remove', label: 'The remove() Method', depth: 0 },
    { id: 'file-directories', label: 'Directories (mkdir, rmdir)', depth: 0 },
    { id: 'file-path-exists', label: 'path.exists() & Path Checks', depth: 0 },
  ],
  'iterators': [
    { id: 'iter-intro', label: 'What is an Iterator?', depth: 0 },
    { id: 'iter-methods', label: '__iter__() and __next__()', depth: 0 },
    { id: 'iter-builtin', label: 'iter() and next() Built-in', depth: 0 },
    { id: 'iter-loop', label: 'Iterator with for Loop', depth: 0 },
    { id: 'iter-manual', label: 'Manual Iteration Pattern', depth: 0 },
    { id: 'iter-create', label: 'Create Custom Iterator', depth: 0 },
    { id: 'iter-stop', label: 'StopIteration', depth: 0 },
  ],
  'generators': [
    { id: 'gen-intro', label: 'What are Generators?', depth: 0 },
    { id: 'gen-why', label: 'Why Generators?', depth: 0 },
    { id: 'gen-create', label: 'Create Generators (yield)', depth: 0 },
    { id: 'gen-yield-return', label: 'yield vs return', depth: 0 },
    { id: 'gen-expression', label: 'Generator Expressions', depth: 0 },
    { id: 'gen-lazy', label: 'Lazy Evaluation', depth: 0 },
  ],
  'try-except-finally': [
    { id: 'try-syntax', label: 'Syntax', depth: 0 },
    { id: 'try-intro', label: 'Try and Except', depth: 0 },
    { id: 'try-exceptions', label: 'What are Exceptions?', depth: 0 },
    { id: 'try-else', label: 'Else Block', depth: 0 },
    { id: 'try-finally', label: 'Finally Block', depth: 0 },
    { id: 'try-multiple', label: 'Multiple Except Blocks', depth: 0 },
    { id: 'try-raise', label: 'raise Statement', depth: 0 },
  ],
  'python-json': [
    { id: 'json-intro', label: 'What is JSON?', depth: 0 },
    { id: 'json-loads', label: 'json.loads()', depth: 0 },
    { id: 'json-load', label: 'json.load()', depth: 0 },
    { id: 'json-dumps', label: 'json.dumps()', depth: 0 },
    { id: 'json-dump', label: 'json.dump()', depth: 0 },
    { id: 'json-pretty', label: 'Pretty Print JSON', depth: 0 },
  ],
  'oop': [],
  'oop-class': [{ id: 'oop-class-content', label: 'Class', depth: 0 }],
  'oop-constructor': [{ id: 'oop-constructor-content', label: 'Constructor', depth: 0 }],
  'oop-inheritance': [{ id: 'oop-inheritance-content', label: 'Inheritance', depth: 0 }],
  'oop-polymorphism': [{ id: 'oop-polymorphism-content', label: 'Polymorphism', depth: 0 }],
  'oop-abstraction': [{ id: 'oop-abstraction-content', label: 'Abstraction', depth: 0 }],
  'oop-encapsulation': [{ id: 'oop-encapsulation-content', label: 'Encapsulation', depth: 0 }],
  'oop-abstract-class': [{ id: 'oop-abstract-class-content', label: 'Abstract Class', depth: 0 }],
};

function PageTocRight({ items }: { items: TocItem[] }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const visibleRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!items.length) return;
    visibleRef.current = new Set();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) visibleRef.current.add(e.target.id);
          else visibleRef.current.delete(e.target.id);
        });
        const ids = items.map((i) => i.id);
        const topVisible = ids.find((id) => visibleRef.current.has(id));
        if (topVisible) setActiveId(topVisible);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current?.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, [items]);

  if (!items.length) return null;
  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <Box
      sx={{
        display: { xs: 'none', lg: 'block' },
        width: 200,
        flexShrink: 0,
        position: 'sticky' as const,
        top: 72,
        maxHeight: 'calc(100vh - 88px)',
        overflowY: 'auto' as const,
        py: 2,
        pl: 2,
        borderLeft: `1px solid ${BORDER}`,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          color: TEXT_MUTED,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          mb: 1.5,
          px: 1,
        }}
      >
        On this page
      </Typography>
      <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {items.map((item) => {
          const isActive = activeId === item.id;
          return (
            <Box
              key={item.id}
              onClick={() => handleClick(item.id)}
              sx={{
                px: 1,
                py: 0.75,
                pl: item.depth === 1 ? 2 : 1,
                cursor: 'pointer',
                borderRadius: '8px',
                fontSize: '0.8rem',
                color: isActive ? PRIMARY : TEXT_MUTED,
                fontWeight: isActive ? 600 : 400,
                bgcolor: isActive ? PRIMARY_LIGHT : 'transparent',
                borderLeft: `2px solid ${isActive ? PRIMARY : 'transparent'}`,
                transition: 'color 0.2s, background 0.2s, border-color 0.2s',
                '&:hover': {
                  color: PRIMARY,
                  bgcolor: PRIMARY_LIGHT,
                  borderLeftColor: PRIMARY,
                },
              }}
            >
              {item.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function VariablesDatatypesView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const imgTransition = isMobile ? { duration: 0 } : { duration: 0.4 };
  const img1Initial = isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 };
  const img2Initial = isMobile ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.98 };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: isMobile ? 0 : 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        Variables & Data Types
      </Typography>

      {/* 1. Variable */}
      <Typography
        component="h2"
        id="variable"
        sx={{
          fontSize: '1.35rem',
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 1.5,
        }}
      >
        1. Variable
      </Typography>
      <motion.div
        initial={img1Initial}
        animate={{ opacity: 1, y: 0 }}
        transition={imgTransition}
        style={{ marginBottom: 16 }}
      >
        <Box
          component="img"
          src="/images/python/what-is-a-variable.png"
          alt="What is a Variable? – idea, measuring, and understanding"
          sx={{
            width: '100%',
            maxWidth: 420,
            height: 'auto',
            display: 'block',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: `1px solid ${BORDER}`,
          }}
        />
      </motion.div>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'flex-start' }, gap: 2, mb: 2 }}>
        <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, flex: 1 }}>
          A variable is a name used to store data in memory. In Python, you do not need to declare the type of a variable explicitly. The type is decided automatically based on the value assigned.
        </Typography>
        <motion.div
          initial={img2Initial}
          animate={{ opacity: 1, scale: 1 }}
          transition={isMobile ? { duration: 0 } : { delay: 0.2, duration: 0.35 }}
          style={{ flexShrink: 0 }}
        >
          <Box
            component="img"
            src="/images/python/variable-stores-data.png"
            alt="Variable as a container storing Data"
            sx={{
              width: '100%',
              maxWidth: 260,
              height: 'auto',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: `1px solid ${BORDER}`,
            }}
          />
        </motion.div>
      </Box>

      {/* Code for variable */}
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, fontSize: '0.95rem' }}>
        Code for variable
      </Typography>
      <Box
        component="pre"
        sx={{
          bgcolor: '#1E293B',
          color: '#E2E8F0',
          p: 2,
          borderRadius: 2,
          overflow: 'auto',
          fontSize: '0.9rem',
          fontFamily: 'monospace',
          mb: 1.5,
        }}
      >
{`x = 10
name = "Reviving India"
price = 99.5`}
      </Box>
      <Box sx={{ mb: 3 }}>
        {[
          { sym: 'x', type: 'integer' },
          { sym: 'name', type: 'string' },
          { sym: 'price', type: 'float' },
        ].map((item, i) => (
          <motion.div
            key={item.sym}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 * i, duration: 0.3 }}
          >
            <Typography component="span" sx={{ color: TEXT_MUTED, fontSize: '0.95rem' }}>
              <strong style={{ color: TEXT_DARK }}>{item.sym}</strong> → {item.type}
            </Typography>
            <br />
          </motion.div>
        ))}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <Typography sx={{ color: TEXT_MUTED, fontStyle: 'italic', mt: 0.5 }}>
            This is called dynamic typing.
          </Typography>
        </motion.div>
      </Box>

      {/* Rules for Naming Variables */}
      <Typography
        component="h3"
        id="rules-naming"
        sx={{ fontSize: '1.1rem', fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2 }}
      >
        Rules for Naming Variables
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1.5 }}>
        A variable name:
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        <li>Must start with a letter (a–z, A–Z) or underscore <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>_</code></li>
        <li>Cannot start with a number</li>
        <li>Can contain letters, numbers, and underscore</li>
        <li>Cannot use Python keywords (if, else, for, class, etc.)</li>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ flex: 1 }}
        >
          <Typography sx={{ fontWeight: 600, color: '#059669', fontSize: '0.9rem', mb: 1 }}>Valid:</Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: '#ECFDF5',
              border: '1px solid #A7F3D0',
              color: '#065F46',
              p: 1.5,
              borderRadius: 2,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
            }}
          >
{`age = 25
_user = "Amit"
total_amount = 500`}
          </Box>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ flex: 1 }}
        >
          <Typography sx={{ fontWeight: 600, color: '#B91C1C', fontSize: '0.9rem', mb: 1 }}>Invalid:</Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: '#FEF2F2',
              border: '1px solid #FECACA',
              color: '#991B1B',
              p: 1.5,
              borderRadius: 2,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
            }}
          >
{`1age = 25      # cannot start with number
class = 10     # keyword not allowed`}
          </Box>
        </motion.div>
      </Box>

      {/* Multiple Variable Assignment */}
      <Typography
        component="h3"
        id="multiple-assignment"
        sx={{ fontSize: '1.1rem', fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2 }}
      >
        Multiple Variable Assignment
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, mb: 0.5, fontSize: '0.95rem' }}>
        <strong>a.</strong> Assign multiple variables at once
      </Typography>
      <Box
        component="pre"
        sx={{
          bgcolor: '#1E293B',
          color: '#E2E8F0',
          p: 1.5,
          borderRadius: 2,
          fontSize: '0.875rem',
          fontFamily: 'monospace',
          mb: 1.5,
        }}
      >
{`a, b, c = 10, 20, 30`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, mb: 0.5, fontSize: '0.95rem' }}>
        <strong>b.</strong> Same value to multiple variables
      </Typography>
      <Box
        component="pre"
        sx={{
          bgcolor: '#1E293B',
          color: '#E2E8F0',
          p: 1.5,
          borderRadius: 2,
          fontSize: '0.875rem',
          fontFamily: 'monospace',
          mb: 2,
        }}
      >
{`x = y = z = 0`}
      </Box>

      {/* Changing Variable Type */}
      <Typography
        component="h3"
        id="changing-type"
        sx={{ fontSize: '1.1rem', fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2 }}
      >
        Changing Variable Type (Dynamic Nature)
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1.5 }}>
        Python allows changing the type anytime:
      </Typography>
      <motion.div
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.15, duration: 0.35 }}
      >
        <Box
          component="pre"
          sx={{
            bgcolor: '#1E293B',
            color: '#E2E8F0',
            p: 2,
            borderRadius: 2,
            fontSize: '0.9rem',
            fontFamily: 'monospace',
            borderLeft: `4px solid ${PRIMARY}`,
            mb: 4,
          }}
        >
{`x = 10      # int
x = "Hello"  # now string`}
        </Box>
      </motion.div>

      {/* 2. Datatypes Classification */}
      <Typography
        component="h2"
        id="data-types"
        sx={{
          fontSize: '1.35rem',
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        2. Datatypes Classification
      </Typography>

      <Typography
        component="h3"
        id="immutable-datatypes"
        sx={{ fontSize: '1.05rem', fontWeight: 600, color: TEXT_DARK, mb: 1 }}
      >
        Immutable Datatypes
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Immutable data types are those whose value cannot be changed in place after creation (e.g. you cannot add, remove, or modify elements in Number, String, and Tuple).
      </Typography>

      {/* Diagram: Python Datatypes → Immutable (Numbers, Strings, Tuples) | Mutable (Lists, Dictionary, Sets) */}
      <Box
        sx={{
          border: `2px solid ${BORDER}`,
          borderRadius: 3,
          p: 3,
          bgcolor: CARD_BG,
          mb: 3,
        }}
      >
        <Typography sx={{ textAlign: 'center', fontWeight: 700, color: PRIMARY, fontSize: '1.1rem', mb: 2 }}>
          Python Datatypes
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ textAlign: 'center', minWidth: 140 }}>
            <Typography sx={{ fontWeight: 600, color: '#059669', fontSize: '0.95rem', mb: 1.5 }}>
              Immutable Datatypes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {['Numbers', 'Strings', 'Tuples'].map((t) => (
                <Box
                  key={t}
                  sx={{
                    py: 1,
                    px: 2,
                    border: '2px solid #E5E7EB',
                    borderRadius: 1,
                    bgcolor: '#F8FAFC',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: TEXT_DARK,
                  }}
                >
                  {t}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', minWidth: 140 }}>
            <Typography sx={{ fontWeight: 600, color: '#059669', fontSize: '0.95rem', mb: 1.5 }}>
              Mutable Datatypes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {['Lists', 'Dictionary', 'Sets'].map((t) => (
                <Box
                  key={t}
                  sx={{
                    py: 1,
                    px: 2,
                    border: '2px solid #E5E7EB',
                    borderRadius: 1,
                    bgcolor: '#F8FAFC',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: TEXT_DARK,
                  }}
                >
                  {t}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Typography
        component="h3"
        id="mutable-datatypes"
        sx={{ fontSize: '1.05rem', fontWeight: 600, color: TEXT_DARK, mb: 1 }}
      >
        Mutable Datatypes
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85 }}>
        Mutable data types are those whose value can be changed in place after creation (e.g. you can add, remove, or modify elements in Lists, Dictionary, and Sets).
      </Typography>
    </motion.div>
  );
}

const codeBlockSx = {
  bgcolor: '#1E293B',
  color: '#E2E8F0',
  p: 2,
  borderRadius: 2,
  overflow: 'auto' as const,
  fontSize: '0.9rem',
  fontFamily: 'monospace',
  mb: 1.5,
};

function NumberTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        1. What are Numbers in Python?
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Numbers in Python are a built-in data type used to store numeric values like integers, decimals, and complex numbers.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Python automatically assigns the numeric type based on the value you provide (dynamic typing).
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`a = 10
b = 3.14
c = 2 + 3j`}
      </Box>

      <Typography
        component="h2"
        id="number-intro"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1.5, mt: 3 }}
      >
        Types of Numbers in Python
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Python has three main numeric types:
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        <li><strong style={{ color: TEXT_DARK }}>Integer (int)</strong></li>
        <li><strong style={{ color: TEXT_DARK }}>Float (float)</strong></li>
        <li><strong style={{ color: TEXT_DARK }}>Complex (complex)</strong></li>
      </Box>

      <Typography
        component="h2"
        id="integer"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        2. Integer (int)
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Definition</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1.5 }}>
        Integers are whole numbers (positive, negative, or zero) without decimal points.
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Examples</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = 10
y = -25
z = 0`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Features</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Unlimited precision (no fixed limit like Java/C++). Can handle very large numbers.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`big_num = 123456789012345678901234567890
print(big_num)`}
      </Box>

      <Typography
        component="h2"
        id="float"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        3. Float (float)
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Definition</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1.5 }}>
        Float represents numbers with decimal points.
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Examples</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`price = 99.99
pi = 3.14159
temperature = -5.6`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Scientific Notation</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = 1.5e3   # 1.5 × 10³ = 1500.0
y = 2.5e-2  # 0.025`}
      </Box>

      <Typography
        component="h2"
        id="complex"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        4. Complex Numbers (complex)
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Definition</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Complex numbers contain a real part and an imaginary part. Format: <strong>a + bj</strong> where j is the imaginary unit.
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Examples</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`c1 = 2 + 3j
c2 = 5 - 2j`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Access real and imaginary parts:
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`c = 3 + 4j
print(c.real)  # 3.0
print(c.imag)  # 4.0`}
      </Box>

      <Typography
        component="h2"
        id="type-checking"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        5. Type Checking of Numbers
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>type()</code> function:
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = 10
y = 3.14
z = 2 + 3j

print(type(x))  # int
print(type(y))  # float
print(type(z))  # complex`}
      </Box>

      <Typography
        component="h2"
        id="type-conversion"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        6. Type Conversion (Type Casting)
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, mb: 0.5, fontSize: '0.95rem' }}><strong>Convert int to float</strong></Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = 10
y = float(x)
print(y)  # 10.0`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, mb: 0.5, fontSize: '0.95rem' }}><strong>Convert float to int</strong></Typography>
      <Box component="pre" sx={codeBlockSx}>
{`a = 5.9
b = int(a)
print(b)  # 5 (decimal removed)`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, mb: 0.5, fontSize: '0.95rem' }}><strong>Convert to complex</strong></Typography>
      <Box component="pre" sx={codeBlockSx}>
{`c = complex(5)
print(c)  # (5+0j)`}
      </Box>

      <Typography
        component="h2"
        id="arithmetic"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        7. Arithmetic Operations on Numbers
      </Typography>
      <Box
        sx={{
          overflow: 'auto',
          border: `1px solid ${BORDER}`,
          borderRadius: 2,
          mb: 2,
        }}
      >
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor: PRIMARY_LIGHT, borderBottom: `2px solid ${BORDER}` }}>
              <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 700, color: TEXT_DARK }}>Operator</Box>
              <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 700, color: TEXT_DARK }}>Meaning</Box>
              <Box component="th" sx={{ p: 1.5, textAlign: 'left', fontWeight: 700, color: TEXT_DARK }}>Example</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {[
              { op: '+', meaning: 'Addition', ex: '5 + 2 = 7' },
              { op: '-', meaning: 'Subtraction', ex: '5 - 2 = 3' },
              { op: '*', meaning: 'Multiplication', ex: '5 * 2 = 10' },
              { op: '/', meaning: 'Division', ex: '5 / 2 = 2.5' },
              { op: '//', meaning: 'Floor Division', ex: '5 // 2 = 2' },
              { op: '%', meaning: 'Modulus', ex: '5 % 2 = 1' },
              { op: '**', meaning: 'Power', ex: '2 ** 3 = 8' },
            ].map((row) => (
              <Box component="tr" key={row.op} sx={{ borderBottom: `1px solid ${BORDER}` }}>
                <Box component="td" sx={{ p: 1.5, color: TEXT_DARK, fontWeight: 600 }}>{row.op}</Box>
                <Box component="td" sx={{ p: 1.5, color: TEXT_MUTED }}>{row.meaning}</Box>
                <Box component="td" sx={{ p: 1.5, color: TEXT_MUTED }}>{row.ex}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ color: TEXT_MUTED, mb: 1, fontSize: '0.95rem' }}>Example:</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`a = 10
b = 3

print(a + b)
print(a / b)
print(a // b)
print(a % b)
print(a ** b)`}
      </Box>

      <Typography
        component="h2"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        8. Important Functions for Numbers
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`abs() – Absolute value
print(abs(-10))  # 10

round() – Rounding
print(round(3.678, 2))  # 3.68

pow() – Power function
print(pow(2, 3))  # 8`}
      </Box>

      <Typography
        component="h2"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        9. Math Module (Advanced Numeric Operations)
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`import math

print(math.sqrt(16))   # 4.0
print(math.ceil(4.2))  # 5
print(math.floor(4.8)) # 4
print(math.pi)         # 3.141592653589793`}
      </Box>

      <Typography
        component="h2"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        10. Mutability of Numbers (Very Important Concept)
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1.5 }}>
        Numbers in Python are IMMUTABLE.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = 10
print(id(x))

x = x + 5
print(id(x))`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Memory address changes — new object created. This is a frequently asked interview question.
      </Typography>

      <Typography
        component="h2"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        11. Number Memory Optimization (Interning Concept)
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Python internally caches small integers (-5 to 256).
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`a = 100
b = 100
print(a is b)  # True (same memory)`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1.5 }}>But:</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`a = 1000
b = 1000
print(a is b)  # May be False`}
      </Box>

      <Typography
        component="h2"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        12. Real-World Examples (Industry)
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Data Engineering Example</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1.5 }}>
        Used in: ETL pipelines (counts, metrics), ML models (floats), Signal processing (complex numbers).
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`records_processed = 1500000
error_rate = 0.02
complex_signal = 3 + 2j`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('variables-datatypes');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Variables & Data Types
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('string');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: String →
        </Button>
      </Box>
    </motion.div>
  );
}

function StringTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        String in Python
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Strings in Python are surrounded by either single quotation marks or double quotation marks. Triple quotes can also be used and are generally used to represent multiline strings.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 3 }}>
        Python does not have a character data type; a single character is simply a string with a length of 1.
      </Typography>

      {/* CRUD overview */}
      <Box
        sx={{
          border: `2px solid ${PRIMARY}`,
          borderRadius: 2,
          p: 2,
          bgcolor: PRIMARY_LIGHT,
          mb: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: TEXT_DARK, mb: 1, fontSize: '1.05rem' }}>
          String CRUD Operations
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { color: TEXT_MUTED, lineHeight: 1.8 } }}>
          <li><strong style={{ color: TEXT_DARK }}>Create</strong> — Single, double, or triple quotes</li>
          <li><strong style={{ color: TEXT_DARK }}>Read</strong> — Access via indexing and slicing</li>
          <li><strong style={{ color: TEXT_DARK }}>Update</strong> — Not allowed (strings are immutable)</li>
          <li><strong style={{ color: TEXT_DARK }}>Insert</strong> — Not in-place; use concatenation</li>
          <li><strong style={{ color: TEXT_DARK }}>Delete</strong> — Entire string with <code>del</code>; not per character</li>
        </Box>
      </Box>

      {/* CREATE */}
      <Typography
        component="h2"
        id="string-create"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1.5, mt: 3 }}
      >
        Create — Ways to Define a String
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Single quotes</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`String1 = 'Welcome to the Reviving India'
print(String1)
print(type(String1))`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Double quotes</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`String1 = "Welcome to the Reviving India"
print(String1)
print(type(String1))`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Triple quotes (multiline)</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`String1 = '''Python Tutorial'''
String2 = """Hello world"""
print(String1, String2)
print(type(String1), type(String2))`}
      </Box>

      {/* READ */}
      <Typography
        component="h2"
        id="string-read"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Read — Access String (Indexing & Slicing)
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1.5 }}>
        Strings can be accessed using indexing. Indexing allows both positive and negative address references: 0 is the first character from the beginning; -1 is the last character, -2 the second last, and so on.
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Complete string</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`String1 = 'Welcome to the Reviving India'
print(String1)   # Complete string`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Single character (indexing)</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`print(String1[0])   # First character: 'W'
print(String1[-1])   # Last character: 'a'`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Multiple characters (slicing)</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`print(String1[0:5])   # 'Welco' (indices 0 to 4)
print(String1[11:18])  # 'Reviving'
print(String1[-5:])    # 'India' (last 5 characters)`}
      </Box>

      {/* UPDATE */}
      <Typography
        component="h2"
        id="string-update"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Update — Not Allowed (Immutable)
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Updating a character in a string is not allowed. Item assignment is not supported and will raise <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>TypeError</code>.
      </Typography>
      <Box
        component="pre"
        sx={{
          ...codeBlockSx,
          borderLeft: '4px solid #B91C1C',
          bgcolor: '#1E293B',
        }}
      >
{`s = "hello"
s[0] = 'H'   # TypeError: 'str' object does not support item assignment`}
      </Box>

      {/* INSERT */}
      <Typography
        component="h2"
        id="string-insert"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Insert — Use Concatenation Instead
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Inserting a character in place is not allowed. You can achieve the effect using string concatenation (<code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>c = a + b</code>).
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`a = "Hello"
b = " World"
c = a + b
print(c)   # 'Hello World'

# Insert in the middle
first = "Reviving"
last = " India"
full = first + last
print(full)   # 'Reviving India'`}
      </Box>

      {/* DELETE */}
      <Typography
        component="h2"
        id="string-delete"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Delete — Entire String Only
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Deletion of the entire string is possible with the <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>del</code> keyword. Deletion of individual characters from a string is not allowed.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`name = "Reviving India"
del name       # OK: entire string deleted
# print(name)  # NameError: name 'name' is not defined`}
      </Box>
      <Box
        component="pre"
        sx={{
          ...codeBlockSx,
          borderLeft: '4px solid #B91C1C',
          bgcolor: '#1E293B',
        }}
      >
{`s = "hello"
del s[0]   # TypeError: 'str' object doesn't support item deletion`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('number');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Number
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('tuple');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Tuple →
        </Button>
      </Box>
    </motion.div>
  );
}

function TupleTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        Tuple in Python
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        A tuple is a collection which is <strong style={{ color: TEXT_DARK }}>ordered</strong> and <strong style={{ color: TEXT_DARK }}>unchangeable</strong>. Tuples are written with round brackets.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        It is a collection of objects which is ordered and immutable. Tuples allow duplicate values.
      </Typography>

      {/* CRUD overview */}
      <Box
        sx={{
          border: `2px solid ${PRIMARY}`,
          borderRadius: 2,
          p: 2,
          bgcolor: PRIMARY_LIGHT,
          mb: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: TEXT_DARK, mb: 1, fontSize: '1.05rem' }}>
          Tuple CRUD Operations
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { color: TEXT_MUTED, lineHeight: 1.8 } }}>
          <li><strong style={{ color: TEXT_DARK }}>Create</strong> — Round brackets, <code>tuple()</code> constructor, or typecasting</li>
          <li><strong style={{ color: TEXT_DARK }}>Read</strong> — Access via indexing and slicing (positive/negative)</li>
          <li><strong style={{ color: TEXT_DARK }}>Update</strong> — Not allowed (immutable); workaround: convert to list, modify, convert back</li>
          <li><strong style={{ color: TEXT_DARK }}>Delete</strong> — Cannot remove items; can delete entire tuple with <code>del</code></li>
        </Box>
      </Box>

      {/* CREATE */}
      <Typography
        component="h2"
        id="tuple-create"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1.5, mt: 3 }}
      >
        Create
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Empty tuple</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`empty_tuple = ()
print(empty_tuple)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Single element</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        To write a tuple containing a single value you have to include a comma, even though there is only one value.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = (50,)
print(x)
print(type(x))`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Multiple elements</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ("apple", "54.23", 23, "apple", "India")
print(x)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Tuple typecasting</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = 'hello'
y = tuple(x)
print(y)   # ('h', 'e', 'l', 'l', 'o')`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>The tuple() constructor</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        It is also possible to use the <code>tuple()</code> constructor to make a tuple. Note the double round brackets.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = tuple(("apple", "54.55", 23))
print(x)`}
      </Box>

      {/* READ */}
      <Typography
        component="h2"
        id="tuple-read"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Read or Access
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1.5 }}>
        To access values in a tuple, use square brackets. For multiple elements use slicing. You can use both positive and negative indexes.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = (10, 20, 30)
print(x[0])      # 10
print(x[0:3])    # (10, 20, 30) — range of indexes
print(x[-1])     # 30 (last element)`}
      </Box>

      {/* UPDATE */}
      <Typography
        component="h2"
        id="tuple-update"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Update
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Tuples are unchangeable: you cannot change, add, or remove items once the tuple is created.
      </Typography>
      <Box
        component="pre"
        sx={{
          ...codeBlockSx,
          borderLeft: '4px solid #B91C1C',
          bgcolor: '#1E293B',
        }}
      >
{`tup1 = (12, 34.56)
tup1[0] = 100   # TypeError: 'tuple' object does not support item assignment`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Workaround — concatenation</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`tup1 = (1, 2)
tup2 = (3, 4)
tup3 = tup1 + tup2
print(tup3)   # (1, 2, 3, 4)`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Workaround — convert to list, modify, convert back</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ("apple", 54.36, 23)
y = list(x)
y[1] = "hello"
x = tuple(y)
print(x)   # ('apple', 'hello', 23)`}
      </Box>

      {/* DELETE */}
      <Typography
        component="h2"
        id="tuple-delete"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Delete
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Tuples are immutable, so you cannot remove items from them. You can delete the entire tuple.
      </Typography>
      <Box
        component="pre"
        sx={{
          ...codeBlockSx,
          borderLeft: '4px solid #B91C1C',
          bgcolor: '#1E293B',
        }}
      >
{`a = (1, 2, 3)
del a[0]   # TypeError: 'tuple' object doesn't support item deletion`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`a = (1, 2, 3)
del a
# print(a)   # NameError: name 'a' is not defined`}
      </Box>

      {/* Packing and unpacking */}
      <Typography
        component="h2"
        id="tuple-packing"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Packing and Unpacking
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        When we create a tuple, we normally assign values to it. This is called &quot;packing&quot; a tuple. We are also allowed to extract the values back into variables — this is called &quot;unpacking&quot;. The number of variables must match the number of values in the tuple.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ("apple", 53.36, 23)
(a, b, c) = x
print(a, b, c)   # apple 53.36 23`}
      </Box>

      {/* Tuple methods */}
      <Typography
        component="h2"
        id="tuple-methods"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1.5, mt: 3 }}
      >
        Tuple Methods
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>len()</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`t = (10, 20, 30)
print(len(t))   # 3`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>count() — tuple.count(value)</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`t = (1, 2, 2, 3, 2)
print(t.count(2))   # 3`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>index() — tuple.index(value)</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Returns the first index of the value. Raises an exception if not found.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`t = (10, 20, 30)
print(t.index(20))   # 1`}
      </Box>

      {/* Concatenation & Multiplication */}
      <Typography
        component="h2"
        id="tuple-concat"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Concatenation and Multiplication
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`t1 = (1, 2)
t2 = (3, 4)
print(t1 + t2)   # (1, 2, 3, 4)
print(t1 * 3)    # (1, 2, 1, 2, 1, 2)`}
      </Box>

      {/* max, min, sum, any */}
      <Typography
        component="h2"
        id="tuple-max-min"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        max(), min(), sum(), any()
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <strong style={{ color: TEXT_DARK }}>max()</strong> — Returns the item with the highest value. Same datatype: numbers compare by value; all strings by highest ASCII. Mixed types raise an error.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <strong style={{ color: TEXT_DARK }}>min()</strong> — Returns the minimum value. <strong style={{ color: TEXT_DARK }}>sum()</strong> — Returns the arithmetic sum of all items; not supported for strings. <strong style={{ color: TEXT_DARK }}>any()</strong> — Returns <code>True</code> if at least one item is truthy; otherwise <code>False</code>.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`t = (10, 20, 5)
print(max(t))   # 20
print(min(t))   # 5
print(sum(t))   # 35

t2 = (0, False, 1)
print(any(t2))  # True`}
      </Box>

      {/* Membership */}
      <Typography
        component="h2"
        id="tuple-membership"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Membership Operations
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Syntax: <code>'string' in / not in (iterable)</code>
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`t = ("apple", "banana", "cherry")
print("apple" in t)    # True
print("mango" not in t)  # True`}
      </Box>

      {/* Nested tuples */}
      <Typography
        component="h2"
        id="tuple-nested"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Nested Tuples
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`a = ((1, 2, 3), (4, (5, 6)))
print(a[1][1][1])   # 6`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('string');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: String
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('list');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: List →
        </Button>
      </Box>
    </motion.div>
  );
}

function ListTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        List in Python
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        List items are <strong style={{ color: TEXT_DARK }}>ordered</strong>, <strong style={{ color: TEXT_DARK }}>changeable</strong>, and allow duplicate values. A list is created by placing all items (Python objects) inside square brackets <code>[]</code>, separated by commas.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Python objects may be of different types (integer, float, string, etc.). Lists in Python can be created by placing the sequence inside square brackets.
      </Typography>

      {/* Image - Python List concept */}
      <Box
        component="img"
        src="https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=800"
        alt="Python programming - Reviving India Python Tutorial"
        sx={{
          width: '100%',
          maxWidth: 500,
          height: 'auto',
          display: 'block',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: `1px solid ${BORDER}`,
          mb: 3,
        }}
      />

      {/* CRUD overview */}
      <Box
        sx={{
          border: `2px solid ${PRIMARY}`,
          borderRadius: 2,
          p: 2,
          bgcolor: PRIMARY_LIGHT,
          mb: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: TEXT_DARK, mb: 1, fontSize: '1.05rem' }}>
          List CRUD Operations
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { color: TEXT_MUTED, lineHeight: 1.8 } }}>
          <li><strong style={{ color: TEXT_DARK }}>Create</strong> — Square brackets, <code>list()</code> constructor, or typecasting</li>
          <li><strong style={{ color: TEXT_DARK }}>Read</strong> — Indexing and slicing (positive/negative, <code>[start:stop:step]</code>)</li>
          <li><strong style={{ color: TEXT_DARK }}>Update</strong> — Refer to index; assign new values or ranges</li>
          <li><strong style={{ color: TEXT_DARK }}>Insert</strong> — <code>append()</code>, <code>insert()</code>, <code>extend()</code>, concatenation</li>
          <li><strong style={{ color: TEXT_DARK }}>Remove</strong> — <code>del</code>, <code>pop()</code>, <code>remove()</code>, <code>clear()</code></li>
        </Box>
      </Box>

      {/* CREATE */}
      <Typography
        component="h2"
        id="list-create"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1.5, mt: 3 }}
      >
        Create
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>List with square brackets</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Reviving India - Python Tutorial example
colors = ['red', 'blue', 'green']
print(colors)

# List with mixed types
topics = ['Python tutorial', 'Reviving India', 4.50, 2024]
print(topics)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Order matters</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Lists are ordered. Changing the order of elements makes them different lists.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`a = [1, 2, "Peter", 4.50, "Ricky", 5, 6]
b = [1, 2, 5, "Peter", 4.50, "Ricky", 6]
print(a == b)   # False — order matters!`}
      </Box>

      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        A list with a single object is sometimes referred to as a <strong style={{ color: TEXT_DARK }}>singleton list</strong>.
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>The list() constructor</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use the <code>list()</code> constructor when creating a new list. Note the double round brackets.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Python Tutorial - list from tuple
x = list(("apple", "banana", "cherry"))
print(x)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Type casting — list(iterable)</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = 'hello'
y = list(x)
print(y)   # ['h', 'e', 'l', 'l', 'o']

# Reviving India — list from string
brand = list("Reviving")
print(brand)   # ['R', 'e', 'v', 'i', 'v', 'i', 'n', 'g']`}
      </Box>

      {/* READ */}
      <Typography
        component="h2"
        id="list-read"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Read or Access
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        List items are indexed. Access them by referring to the index number. Positive index starts with 0 (first element). Negative index starts with -1 (last element). Use slicing <code>[:]</code> or <code>[start:stop:step]</code> for multiple elements.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`courses = ["Python tutorial", "Reviving India", "Data Science", 99.99]
print(courses[0])      # Python tutorial
print(courses[-1])     # 99.99 (last element)
print(courses[0:2])    # ['Python tutorial', 'Reviving India']
print(courses[::2])    # step 2: ['Python tutorial', 'Data Science']`}
      </Box>

      {/* UPDATE */}
      <Typography
        component="h2"
        id="list-update"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Update
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Refer to the index number to update. You can also mention a range of indexes.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ["apple", 56.32, 23]
x[0] = "blackcurrant"
print(x)   # ['blackcurrant', 56.32, 23]

# Range of indexes
x[1:3] = [99.99, "watermelon"]
print(x)`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        If you specify <strong>more</strong> elements during update, the remaining values are inserted. If you specify <strong>fewer</strong> items than you replace, you lose particular index values.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ["a", "b", "c"]
x[1:2] = [99.99, "watermelon"]   # inserts 2 items into slice of 1
print(x)   # ['a', 99.99, 'watermelon', 'c']`}
      </Box>

      {/* INSERT */}
      <Typography
        component="h2"
        id="list-insert"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Insert New Elements
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>append() — adds item to the end</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ["Hello", "banana", 23]
x.append("orange")
print(x)   # ['Hello', 'banana', 23, 'orange']

# Reviving India example
topics = ["Python tutorial", "Data Science"]
topics.append("Reviving India")
print(topics)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Concatenation — List C = List A + List B</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`list_a = ["Python", "Reviving"]
list_b = ["India", "Tutorial"]
list_c = list_a + list_b
print(list_c)   # ['Python', 'Reviving', 'India', 'Tutorial']`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>insert() — inserts at specified index</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Syntax: <code>x.insert(index, value)</code>
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ["apple", "cherry"]
x.insert(1, "banana")
print(x)   # ['apple', 'banana', 'cherry']`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>extend() — append elements from another list</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Syntax: <code>x.extend(y)</code>. You can extend any iterable (tuples, sets, dictionaries, etc.).
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ["Python tutorial"]
y = ["Reviving India", "Data Science"]
x.extend(y)
print(x)   # ['Python tutorial', 'Reviving India', 'Data Science']`}
      </Box>

      {/* REMOVE */}
      <Typography
        component="h2"
        id="list-remove"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Remove
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>del</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ["a", "b", "c", "d"]
del x[1]           # Single item deletion
print(x)           # ['a', 'c', 'd']

del x[0:2]         # Multiple item deletion (slice)
print(x)           # ['d']

# del x             # Entire list deletion — x no longer defined`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>pop() — removes specified index</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Syntax: <code>x.pop(index)</code>. If you omit the index, it removes the last element.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ["apple", "banana", "cherry"]
x.pop(1)
print(x)   # ['apple', 'cherry']

x.pop()    # removes last
print(x)   # ['apple']`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>remove() — removes specified value</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Syntax: <code>x.remove(value)</code>
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ["Reviving India", "Python tutorial", "Reviving India"]
x.remove("Reviving India")   # removes first occurrence
print(x)   # ['Python tutorial', 'Reviving India']`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>clear() — empties the list</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The list still exists but has no content. Syntax: <code>x.clear()</code>
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = ["a", "b", "c"]
x.clear()
print(x)   # []`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('tuple');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Tuple
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('dictionary');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Dictionary →
        </Button>
      </Box>
    </motion.div>
  );
}

function DictionaryTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        Dictionary in Python
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        A dictionary consists of a collection of <strong style={{ color: TEXT_DARK }}>key-value pairs</strong>. Dictionary items are <strong style={{ color: TEXT_DARK }}>unordered</strong>, <strong style={{ color: TEXT_DARK }}>changeable</strong>, and does not allow <strong style={{ color: TEXT_DARK }}>duplicate keys</strong>.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Dictionaries cannot have two items with the same key. To create a dictionary, declare key-value pairs enclosing a comma-separated list in curly braces <code>{'{}'}</code>. A colon <code>:</code> separates each key from its associated value.
      </Typography>

      {/* Image - Python Dictionary concept */}
      <Box
        component="img"
        src="https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=800"
        alt="Python Dictionary - Reviving India Python Tutorial"
        sx={{
          width: '100%',
          maxWidth: 500,
          height: 'auto',
          display: 'block',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: `1px solid ${BORDER}`,
          mb: 3,
        }}
      />

      {/* Dictionary Operations overview */}
      <Box
        sx={{
          border: `2px solid ${PRIMARY}`,
          borderRadius: 2,
          p: 2,
          bgcolor: PRIMARY_LIGHT,
          mb: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: TEXT_DARK, mb: 1, fontSize: '1.05rem' }}>
          Dictionary Operations
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { color: TEXT_MUTED, lineHeight: 1.8 } }}>
          <li><strong style={{ color: TEXT_DARK }}>Create</strong> — Curly braces <code>{'{}'}</code>, <code>dict()</code> constructor, nested dictionaries</li>
          <li><strong style={{ color: TEXT_DARK }}>Access</strong> — Square brackets <code>[]</code>, <code>get()</code>, <code>keys()</code>, <code>values()</code>, <code>items()</code></li>
          <li><strong style={{ color: TEXT_DARK }}>Update</strong> — Assign by key, <code>update()</code> method</li>
          <li><strong style={{ color: TEXT_DARK }}>Remove</strong> — <code>pop()</code>, <code>popitem()</code>, <code>del</code>, <code>clear()</code></li>
          <li><strong style={{ color: TEXT_DARK }}>Other</strong> — <code>copy()</code>, <code>fromkeys()</code>, <code>setdefault()</code>, dictionary comprehension</li>
        </Box>
      </Box>

      {/* Key Points */}
      <Box
        sx={{
          bgcolor: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: TEXT_DARK, mb: 1, fontSize: '0.95rem' }}>
          Important: Keys vs Values
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { color: TEXT_MUTED, lineHeight: 1.8 } }}>
          <li><strong style={{ color: TEXT_DARK }}>Keys</strong> must be <strong style={{ color: TEXT_DARK }}>immutable</strong> (strings, numbers, tuples)</li>
          <li><strong style={{ color: TEXT_DARK }}>Values</strong> can be both <strong style={{ color: TEXT_DARK }}>mutable and immutable</strong> (any data type)</li>
        </Box>
      </Box>

      {/* CREATE */}
      <Typography
        component="h2"
        id="dict-create"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1.5, mt: 3 }}
      >
        Creating a Dictionary
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Basic dictionary creation</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Reviving India - Python Tutorial example
mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
print(mydict)
# Output: {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Creating an empty dictionary</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Creating an empty Dictionary
mydict = {}
print("Empty Dictionary: ")
print(mydict)
# Output: Empty Dictionary: 
# {}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Nested Dictionary</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        A dictionary can contain another dictionary as a value.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Nested Dictionary
mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin', 4: {'4a': 'hello', '4b': 'world'}}
print(mydict)
# Output: {1: 'Reviving', 'a': 'India', 34.5: 'Revin', 4: {'4a': 'hello', '4b': 'world'}}

# Accessing nested dictionary
print(mydict[4]['4a'])   # hello`}
      </Box>

      {/* ACCESS */}
      <Typography
        component="h2"
        id="dict-access"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Access Dictionary Items
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The items of a dictionary can be accessed by referring to its key name, inside square brackets.
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Using square brackets []</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
print(mydict[1])      # Reviving
print(mydict['a'])    # India`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Using get() method</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>get()</code> method is also used to get the value of an existing key in the dictionary.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = mydict.get(1)
print(x)   # Reviving`}
      </Box>

      <Box
        sx={{
          bgcolor: '#FEE2E2',
          border: '1px solid #EF4444',
          borderRadius: 2,
          p: 2,
          mb: 2,
        }}
      >
        <Typography sx={{ color: TEXT_DARK, fontSize: '0.9rem' }}>
          <strong>Note:</strong> If you use square brackets <code>[]</code>, a <code>KeyError</code> is raised when the key is not found. On the other hand, the <code>get()</code> method returns <code>None</code> if the key is not found.
        </Typography>
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Access all the keys — keys()</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>keys()</code> method will return a list of all the keys in the dictionary.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
print(mydict.keys())
# Output: dict_keys([1, 'a', 34.5])`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Access all the values — values()</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>values()</code> method will return a list of all the values in the dictionary.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
print(mydict.values())
# Output: dict_values(['Reviving', 'India', 'Revin'])`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Access key-value pairs — items()</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>items()</code> method will return each item in a dictionary, as tuples in a list.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
print(mydict.items())
# Output: dict_items([(1, 'Reviving'), ('a', 'India'), (34.5, 'Revin')])`}
      </Box>

      {/* UPDATE */}
      <Typography
        component="h2"
        id="dict-update"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Update Dictionary Items
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The value of a specific item or key can be changed by referring to its key name.
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Update by key assignment</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
mydict[1] = '2021'
print(mydict)
# Output: {1: '2021', 'a': 'India', 34.5: 'Revin'}`}
      </Box>

      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        If the key does not exist in the dictionary, it will <strong style={{ color: TEXT_DARK }}>add the key-value pair</strong> rather than update.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India'}
mydict['new_key'] = 'new_value'
print(mydict)
# Output: {1: 'Reviving', 'a': 'India', 'new_key': 'new_value'}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Using update() method</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>update()</code> method will update the dictionary with the items from the given argument.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India'}
mydict.update({1: 58})
print(mydict)
# Output: {1: 58, 'a': 'India'}

# Adding multiple items
mydict.update({'b': 'Python', 'c': 'Tutorial'})
print(mydict)
# Output: {1: 58, 'a': 'India', 'b': 'Python', 'c': 'Tutorial'}`}
      </Box>

      {/* REMOVE */}
      <Typography
        component="h2"
        id="dict-remove"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Remove Dictionary Items
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>pop() — removes item with specified key</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
mydict.pop(1)
print(mydict)
# Output: {'a': 'India', 34.5: 'Revin'}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>popitem() — removes the last inserted item</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
mydict.popitem()
print(mydict)
# Output: {1: 'Reviving', 'a': 'India'}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>del — removes item with specified key</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
del mydict[1]
print(mydict)
# Output: {'a': 'India', 34.5: 'Revin'}

# del mydict   # Deletes the entire dictionary`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>clear() — removes all elements</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Syntax: <code>dictionary.clear()</code> — No parameters required.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}
mydict.clear()
print(mydict)
# Output: {}`}
      </Box>

      {/* COPY */}
      <Typography
        component="h2"
        id="dict-copy"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        copy() Method
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>copy()</code> method returns a copy of the specified dictionary. Syntax: <code>dictionary.copy()</code> — No parameters required.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India'}
new_dict = mydict.copy()
print(new_dict)
# Output: {1: 'Reviving', 'a': 'India'}`}
      </Box>

      {/* FROMKEYS */}
      <Typography
        component="h2"
        id="dict-fromkeys"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        fromkeys() Method
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>fromkeys()</code> method returns a dictionary with the specified keys and the specified value. Syntax: <code>dict.fromkeys(keys, value)</code>
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`keys = ('a', 'b', 'c')
value = 0
mydict = dict.fromkeys(keys, value)
print(mydict)
# Output: {'a': 0, 'b': 0, 'c': 0}

# Without value (defaults to None)
mydict2 = dict.fromkeys(keys)
print(mydict2)
# Output: {'a': None, 'b': None, 'c': None}`}
      </Box>

      {/* SETDEFAULT */}
      <Typography
        component="h2"
        id="dict-setdefault"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        setdefault() Method
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>setdefault()</code> method returns the value of the item with the specified key. If the key does not exist, insert the key with the specified value. Syntax: <code>dictionary.setdefault(keyname, value)</code>
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India'}

# Key exists
x = mydict.setdefault(1, 'default')
print(x)         # Reviving
print(mydict)    # {1: 'Reviving', 'a': 'India'}

# Key does not exist
y = mydict.setdefault('b', 'Python')
print(y)         # Python
print(mydict)    # {1: 'Reviving', 'a': 'India', 'b': 'Python'}`}
      </Box>

      {/* CHECK KEY EXISTS */}
      <Typography
        component="h2"
        id="dict-check-key"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Check if Key Exists
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use the <code>in</code> keyword to check if a key exists in the dictionary.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mydict = {1: 'Reviving', 'a': 'India', 34.5: 'Revin'}

# Check if key exists
if 1 in mydict:
    print("Key 1 exists")
# Output: Key 1 exists

if 'z' not in mydict:
    print("Key 'z' does not exist")
# Output: Key 'z' does not exist`}
      </Box>

      {/* DICTIONARY COMPREHENSION */}
      <Typography
        component="h2"
        id="dict-comprehension"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Dictionary Comprehension
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Dictionary comprehension consists of an expression pair <code>(key: value)</code> followed by a <code>for</code> statement inside curly braces <code>{'{}'}</code>.
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Basic dictionary comprehension</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Dictionary Comprehension
squares = {x: x*x for x in range(4)}
print(squares)
# Output: {0: 0, 1: 1, 2: 4, 3: 9}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Dictionary comprehension with if conditional</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Dictionary Comprehension with if conditional
odd_squares = {x: x*x for x in range(11) if x % 2 == 1}
print(odd_squares)
# Output: {1: 1, 3: 9, 5: 25, 7: 49, 9: 81}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Creating dictionary from two lists</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Creating dictionary from two lists using zip
keys = ['name', 'age', 'city']
values = ['Reviving India', 25, 'Delhi']
mydict = {k: v for k, v in zip(keys, values)}
print(mydict)
# Output: {'name': 'Reviving India', 'age': 25, 'city': 'Delhi'}`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('tuple');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Tuple
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('sets');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Sets →
        </Button>
      </Box>
    </motion.div>
  );
}

function SetsTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        Sets in Python
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        A <strong style={{ color: TEXT_DARK }}>set</strong> is an <strong style={{ color: TEXT_DARK }}>unordered</strong> collection data type that is <strong style={{ color: TEXT_DARK }}>iterable</strong>, <strong style={{ color: TEXT_DARK }}>mutable</strong>, and has <strong style={{ color: TEXT_DARK }}>no duplicate elements</strong>.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        A set is a collection which is both <strong style={{ color: TEXT_DARK }}>unordered</strong> and <strong style={{ color: TEXT_DARK }}>unindexed</strong>. Sets are written with <strong style={{ color: TEXT_DARK }}>curly brackets</strong> <code>{'{}'}</code>.
      </Typography>

      {/* Set Operations overview */}
      <Box
        sx={{
          border: `2px solid ${PRIMARY}`,
          borderRadius: 2,
          p: 2,
          bgcolor: PRIMARY_LIGHT,
          mb: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: TEXT_DARK, mb: 1, fontSize: '1.05rem' }}>
          Set Operations (CRUD-style)
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { color: TEXT_MUTED, lineHeight: 1.8 } }}>
          <li><strong style={{ color: TEXT_DARK }}>Create</strong> — Curly braces <code>{'{}'}</code>, <code>set()</code> from sequence; empty set with <code>set()</code></li>
          <li><strong style={{ color: TEXT_DARK }}>Read / Access</strong> — No index or key; use a loop to get elements</li>
          <li><strong style={{ color: TEXT_DARK }}>Add</strong> — <code>add()</code> (one item), <code>update()</code> (multiple / from another set or iterable)</li>
          <li><strong style={{ color: TEXT_DARK }}>Remove</strong> — <code>remove()</code>, <code>discard()</code>, <code>pop()</code>, <code>clear()</code>, <code>del</code></li>
          <li><strong style={{ color: TEXT_DARK }}>Join Sets</strong> — <code>union()</code> (or <code>|</code>), <code>intersection()</code> (or <code>&</code>), <code>difference()</code> (or <code>-</code>), <code>symmetric_difference</code></li>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: TEXT_DARK, mb: 1, fontSize: '0.95rem' }}>
          Important
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { color: TEXT_MUTED, lineHeight: 1.8 } }}>
          <li>Sets do <strong style={{ color: TEXT_DARK }}>not</strong> allow duplicate values; duplicates are automatically removed.</li>
          <li><strong style={{ color: TEXT_DARK }}>Mutable elements</strong> (list, dictionary, set) cannot be members of a set—only hashable (immutable) types are allowed.</li>
        </Box>
      </Box>

      {/* CREATE */}
      <Typography
        component="h2"
        id="sets-create"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1.5, mt: 3 }}
      >
        Create a Set
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        A set can be created by enclosing comma-separated items in curly braces <code>{'{}'}</code>. Python also provides the <code>set()</code> method, which creates a set from the passed sequence (e.g. a list).
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Using curly braces</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset = {"revin", "reviving", 54, 54.36}
print(myset)
# Output (order may vary): {'revin', 'reviving', 54, 54.36}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Using set() from a sequence</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = set([1, 2, 3, 4])
print(x, type(x))
# Output: {1, 2, 3, 4} <class 'set'>`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Empty set</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Creating an empty set is different from dictionaries: empty curly braces <code>{'{}'}</code> create an empty <strong>dictionary</strong>. Use <code>set()</code> with no arguments to create an empty set.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Empty set using set() function
myset = set()
print(type(myset))
# Output: <class 'set'>`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Sets do not allow duplicates</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset = {"revin", "revin", 54, 54}
print(myset)
# Output: {'revin', 54}`}
      </Box>

      {/* READ / ACCESS */}
      <Typography
        component="h2"
        id="sets-read"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Read or Access Set Items
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        In a set you cannot access elements by index or key. You can only <strong style={{ color: TEXT_DARK }}>loop</strong> to get the elements.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset = {"revin", "reviving", 54, 54.36}
for var in myset:
    print(var)
# Output (order may vary): revin, reviving, 54, 54.36`}
      </Box>

      {/* ADD */}
      <Typography
        component="h2"
        id="sets-add"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Add or Change
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        You cannot change existing items in a set, but you can <strong style={{ color: TEXT_DARK }}>add</strong> new items using <code>add()</code> and <code>update()</code>.
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>add() — add one item</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use the <code>add()</code> method to add a single item to the set.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset = {"revin", "reviving", 54, 54.36}
myset.add("hello world")
print(myset)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>update() — add items from another set or iterable</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use <code>update()</code> to add items from another set (or any iterable) into the current set. <strong>Note:</strong> <code>update()</code> can take any iterable (set, list, tuple, etc.).
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset1 = {"revin", "reviving", 54, 54.36}
myset2 = {1, 2}
myset1.update(myset2)
print(myset1)
# myset1 now contains all elements from both sets`}
      </Box>

      {/* REMOVE */}
      <Typography
        component="h2"
        id="sets-remove"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Remove Set Items
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        To remove an item from a set, you can use: <code>remove()</code>, <code>discard()</code>, <code>pop()</code>, <code>clear()</code>, or <code>del</code>.
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>remove(element)</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Removes the specified element. Raises <code>KeyError</code> if the element is not found.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset = {"revin", "reviving", 54}
myset.remove("reviving")
print(myset)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>discard(element)</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Removes the element if present; does nothing (no error) if the element is not in the set.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset = {"revin", "reviving", 54}
myset.discard("reviving")
myset.discard("missing")   # No error
print(myset)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>pop()</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Removes and returns an arbitrary element. Raises <code>KeyError</code> if the set is empty.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset = {"revin", "reviving", 54}
x = myset.pop()
print(x, myset)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>clear() and del</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>clear()</code> removes all elements from the set. <code>del</code> deletes the set variable entirely.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset = {"revin", "reviving", 54}
myset.clear()
print(myset)   # set()

# del myset   # Deletes the entire set; name no longer exists`}
      </Box>

      {/* JOIN SETS */}
      <Typography
        component="h2"
        id="sets-join"
        sx={{ fontSize: '1.35rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Join Sets
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>union() — or pipe operator |</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Returns a <strong>new</strong> set containing all items from both sets. Duplicates are excluded.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset1 = {"revin", "reviving", 54, 54.36}
myset2 = {1, 2, 3}
set3 = myset1.union(myset2)
print(set3)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>update() vs union()</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>update()</code> inserts the items from the second set <strong>into the first set</strong> (modifies in place). <code>union()</code> returns a new set and does not change the original.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`myset1 = {"revin", "reviving", 54, 54.36, 1}
myset2 = {1, 2, 3}
myset1.update(myset2)
print(myset1)
# Both union() and update() exclude duplicate items`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>intersection_update() and intersection() — & operator</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <strong>intersection_update(y):</strong> Keeps only the items that are present in <strong>both</strong> sets; modifies the original set. <strong>intersection(y):</strong> Returns a <strong>new</strong> set containing only items present in both sets.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = {"a", "b", "c"}
y = {"c", "d", "e"}
x.intersection_update(y)
print(x)   # {'c'}

# intersection() returns new set
a = {"a", "b", "c"}
b = {"c", "d", "e"}
z = a.intersection(b)
print(z)   # {'c'}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>symmetric_difference_update()</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Keeps only the elements that are <strong>not</strong> present in both sets (modifies the set in place).
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = {"a", "b", "c"}
y = {"c", "d", "e"}
x.symmetric_difference_update(y)
print(x)   # {'a', 'b', 'd', 'e'}`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>difference() — subtraction (-) operator</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Returns elements that are in the first set but not in the second. Syntax: <code>set1.difference(set2)</code> or <code>set1 - set2</code>.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`Day1 = {"Mon", "Tues", "Wed", "Thurs"}
Day2 = {"Mon", "Tues", "Sun"}
print(Day2.difference(Day1))
# Output: {'Sun'}

print(Day1.difference(Day2))
# Output: {'Wed', 'Thurs'}`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('dictionary');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Dictionary
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('conditions-loops');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Conditions & Loops →
        </Button>
      </Box>
    </motion.div>
  );
}

function ConditionsLoopsView({ onNavigate, activeId }: { onNavigate: (topicId: string) => void; activeId?: string }) {
  useEffect(() => {
    if (activeId === 'conditional-statements' || activeId === 'python-loops') {
      const el = document.getElementById(activeId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeId]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        Conditions & Loops
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 4 }}>
        Master Python decision-making and iteration with conditional statements and loops. These are essential building blocks for controlling program flow, handling logic, and automating repetitive tasks.
      </Typography>

      {/* Section 1: Conditional Statements */}
      <Typography
        component="h2"
        id="conditional-statements"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        1. Conditional Statements
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Decision making statements in programming languages decide the direction of flow of program execution. Decision making statements available in Python are also known as <strong>if..else statements</strong>.
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, fontSize: '1rem' }}>Versions of If-else Statements</Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        <li>if statement</li>
        <li>if..else statements</li>
        <li>nested if statements</li>
        <li>if-elif ladder</li>
        <li>Short Hand if-else statement</li>
      </Box>

      <Typography
        component="h3"
        id="if-statement"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        if statement
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        It is used to decide whether a certain statement or block of statements will be executed or not.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`if condition:
    # Statements to execute if condition is true`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Example: Check if a number is positive</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`num = 10
if num > 0:
    print("The number is positive")

# Output: The number is positive`}
      </Box>

      <Typography
        component="h3"
        id="if-else"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        if-else
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>else</code> statement is used with <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>if</code> to execute a block of code when the condition is false.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`if condition:
    # Executes this block if condition is true
else:
    # Executes this block if condition is false`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Example: Check even or odd number</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`num = 7
if num % 2 == 0:
    print("Even number")
else:
    print("Odd number")

# Output: Odd number`}
      </Box>

      <Typography
        component="h3"
        id="nested-if"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        nested if
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Python allows us to nest if statements within if statements for more complex conditions.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`if condition1:
    # Executes when condition1 is true
    if condition2:
        # Executes when condition2 is true`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Example: Grade classification</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`score = 85
if score >= 60:
    if score >= 90:
        print("Grade: A")
    else:
        print("Grade: B or C")
else:
    print("Grade: Fail")`}
      </Box>

      <Typography
        component="h3"
        id="if-elif-else"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        if-elif-else ladder
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The if statements are executed from the top down. As soon as one of the conditions controlling the if is true, the statement associated with that if is executed, and the rest of the ladder is bypassed. If none of the conditions is true, then the final else statement will be executed.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`if condition:
    statement
elif condition:
    statement
else:
    statement`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Example: Day of week (1-7)</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`day = 3
if day == 1:
    print("Monday")
elif day == 2:
    print("Tuesday")
elif day == 3:
    print("Wednesday")
else:
    print("Thursday to Sunday")

# Output: Wednesday`}
      </Box>

      <Typography
        component="h3"
        id="shorthand-if-else"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Short Hand if-else statement
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Syntax: <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>statement_True if condition else statement_False</code>. This can be used to write if-else statements in a single line.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`age = 20
status = "Adult" if age >= 18 else "Minor"
print(status)  # Output: Adult

# Another example: find maximum of two numbers
a, b = 5, 10
max_val = a if a > b else b
print(max_val)  # Output: 10`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Real-world Example: User role check</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`user_role = "admin"
if user_role == "admin":
    print("Full access granted")
elif user_role == "user":
    print("Standard access")
else:
    print("Access denied")`}
      </Box>

      {/* Section 2: Python Loops */}
      <Typography
        component="h2"
        id="python-loops"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 5 }}
      >
        2. Python Loops
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        A loop statement allows us to execute a statement or group of statements multiple times. A loop is used to iterate over Python objects and perform any logic with the data.
      </Typography>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, fontSize: '1rem' }}>Types of Loops</Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        <li>While Loop</li>
        <li>For Loop</li>
        <li>Nested Loop</li>
      </Box>

      <Typography
        component="h3"
        id="while-loop"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        While Loop
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>while</code> loop can execute a set of statements as long as a condition is true. It is used when we don&apos;t know the number of iterations in advance.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`while expression:
    statement(s)`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Example: Countdown from 5</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`count = 5
while count > 0:
    print(count)
    count -= 1
print("Liftoff!")

# Output: 5, 4, 3, 2, 1, Liftoff!`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>While with else</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>else</code> block runs once when the condition no longer is true.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`while expression:
    statement(s)
else:
    statement(s)`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`n = 3
while n > 0:
    print(n)
    n -= 1
else:
    print("Loop ended normally")

# Output: 3, 2, 1, Loop ended normally`}
      </Box>

      <Typography
        component="h3"
        id="for-loop"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        For Loop
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        A <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>for</code> loop is used for iterating over a sequence or iterable object like list, tuple, string, etc.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`for iterating_var in sequence:
    statement(s)`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Example: Iterate over a list</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`fruits = ["apple", "banana", "cherry"]
for fruit in fruits:
    print(fruit)

# Output: apple, banana, cherry`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>The range() Function</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>range()</code> function returns a sequence of numbers, starting from 0 by default, and increments by 1.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`for x in range(7):
    print(x)

# Output: 0, 1, 2, 3, 4, 5, 6`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, mb: 1, fontSize: '0.95rem' }}>range() with start and step: <code>range(start, stop, step)</code></Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# range(2, 10, 2) -> 2, 4, 6, 8
for i in range(2, 10, 2):
    print(i)

# Output: 2, 4, 6, 8`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Else in For Loop</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>else</code> keyword in a for loop specifies a block of code to be executed when the loop finishes without a break.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`for iterating_var in sequence:
    statement(s)
else:
    statement(s)`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`for i in range(3):
    print(i)
else:
    print("Loop completed")

# Output: 0, 1, 2, Loop completed`}
      </Box>

      <Typography
        component="h3"
        id="nested-loops"
        sx={{ fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK, mb: 1, mt: 3 }}
      >
        Nested Loops
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The "inner loop" will be executed one time for each iteration of the "outer loop".
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`for iterating_var in sequence:
    for iterating_var in sequence:
        statement(s)
    statement(s)`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>Example: Multiplication table</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`for i in range(1, 4):
    for j in range(1, 4):
        print(f"{i} x {j} = {i*j}", end="  ")
    print()

# Output:
# 1 x 1 = 1  1 x 2 = 2  1 x 3 = 3
# 2 x 1 = 2  2 x 2 = 4  2 x 3 = 6
# 3 x 1 = 3  3 x 2 = 6  3 x 3 = 9`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Real-world Example: Iterate over list of tuples</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`students = [("Alice", 85), ("Bob", 92), ("Charlie", 78)]
for name, score in students:
    grade = "A" if score >= 90 else "B" if score >= 80 else "C"
    print(f"{name}: {grade}")`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('sets');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Sets
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('functions');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Functions →
        </Button>
      </Box>
    </motion.div>
  );
}

function FunctionsTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        Python Functions
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 4 }}>
        A function is a block of organized, reusable code that is used to perform a single, related action. A function is a block of code which only runs when it is called.
      </Typography>

      {/* Syntax */}
      <Typography
        component="h2"
        id="func-syntax"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Syntax of Function
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def function_name(parameters):
    """docstring"""
    statement(s)
    return [expression]`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        The keyword <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>def</code> marks the start of the function header. A colon (<code>:</code>) marks the end of the function header.
      </Typography>

      {/* Creating a Function */}
      <Typography
        component="h2"
        id="func-create"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Creating a Function
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        In Python a function is defined using the <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>def</code> keyword:
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def my_func():
    print("Welcome to Reviving India")`}
      </Box>

      {/* Calling a Function */}
      <Typography
        component="h2"
        id="func-calling"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Calling a Function
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        To call a function, use the function name followed by parentheses:
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`my_func()

# Output: Welcome to Reviving India`}
      </Box>

      {/* Parameters */}
      <Typography
        component="h2"
        id="func-parameters"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Parameters
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Parameters are specified after the function name, inside the parentheses. Add as many parameters as you want, just separate them with a comma.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def my_func(country):
    print("I am from " + country)

my_func("India")   # Output: I am from India
my_func("Brazil")  # Output: I am from Brazil`}
      </Box>

      {/* Arbitrary Arguments *args */}
      <Typography
        component="h2"
        id="func-args"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Arbitrary Arguments, *args
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        If you don&apos;t know how many arguments will be passed into your function, add a <code>*</code> before the parameter name in the function definition.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def my_func(*classes):
    print(classes)

my_func("first", "second", "third")
# Output: ('first', 'second', 'third')`}
      </Box>

      {/* Keyword Arguments */}
      <Typography
        component="h2"
        id="func-keyword"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Keyword Arguments
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Arguments with the <code>key = value</code> syntax are called keyword arguments.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def my_func(a, b):
    print(a, b)

my_func(a=7, b=8)
# Output: 7 8`}
      </Box>

      {/* Arbitrary Keyword Arguments **kwargs */}
      <Typography
        component="h2"
        id="func-kwargs"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Arbitrary Keyword Arguments, **kwargs
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        If you don&apos;t know how many keyword arguments will be passed into your function, add two asterisks <code>**</code> before the parameter name in the function definition.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def my_func(**classes):
    print(classes['a'])

my_func(a="first", b="second", c="third")
# Output: first`}
      </Box>

      {/* Default Parameter Value */}
      <Typography
        component="h2"
        id="func-default"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Default Parameter Value
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        If the function is called without an argument, it uses the default value.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def my_func(lang="Python"):
    print("I am learning " + lang)

my_func("C++")  # Output: I am learning C++
my_func("Java") # Output: I am learning Java
my_func()       # Output: I am learning Python`}
      </Box>

      {/* Return Values */}
      <Typography
        component="h2"
        id="func-return"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Return Values
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        To let a function return a value, use the <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>return</code> statement:
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def sqrt(x):
    return x * x

print(sqrt(3))  # Output: 9
print(sqrt(5))  # Output: 25`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, fontSize: '0.95rem' }}>Return multiple values (as tuple)</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def sqrt_cube(x):
    return x*x, x*x*x

print(sqrt_cube(3))  # Output: (9, 27)
print(sqrt_cube(5))  # Output: (25, 125)`}
      </Box>

      {/* Docstrings */}
      <Typography
        component="h2"
        id="func-docstring"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Docstrings
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        A docstring is briefly used to explain what a function does. The first string after the function header is called the docstring. This string is available as the <code>__doc__</code> attribute of the function.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def greet(name):
    """Prints a greeting message for the given name."""
    print(f"Hello, {name}!")

print(greet.__doc__)
# Output: Prints a greeting message for the given name.`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('conditions-loops');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Conditions & Loops
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('python-lambda');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Lambda Function →
        </Button>
      </Box>
    </motion.div>
  );
}

function PythonLambdaTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        Lambda Function
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 4 }}>
        A function defined without a name is called a <strong>Lambda function</strong> or <strong>anonymous function</strong>. It can take any number of arguments but can only have one expression. Lambda functions are widely used with <code>filter()</code>, <code>map()</code>, and <code>reduce()</code> for concise data transformations.
      </Typography>

      {/* Syntax */}
      <Typography
        component="h2"
        id="lambda-intro"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Lambda (Anonymous Function)
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Lambda functions are small anonymous functions. They are useful when you need a simple function for a short period, such as in callbacks or with built-in functions.
      </Typography>

      <Typography
        component="h2"
        id="lambda-syntax"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Syntax
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`lambda arguments : expression`}
      </Box>

      {/* Single Argument */}
      <Typography
        component="h2"
        id="lambda-single"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Single Argument
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Square function
x = lambda a : a * a
print(x(5))

# Output: 25`}
      </Box>

      {/* Multiple Arguments */}
      <Typography
        component="h2"
        id="lambda-multiple"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Multiple Arguments
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`x = lambda a, b : a * b
print(x(5, 6))

# Output: 30`}
      </Box>

      {/* Lambda with Built-in: filter */}
      <Typography
        component="h2"
        id="lambda-filter"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        filter() with Lambda
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>filter()</code> function takes two arguments: a lambda function and a list. It returns an iterator with elements for which the lambda returns <code>True</code>.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`li = [5, 7, 20, 66, 58, 99, 63, 44]
final_list = list(filter(lambda x: (x % 2 != 0), li))
print(final_list)

# Output: [5, 7, 99, 63]`}
      </Box>

      {/* Lambda with Built-in: map */}
      <Typography
        component="h2"
        id="lambda-map"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        map() with Lambda
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>map()</code> function takes two arguments: a lambda function and a list. It returns a new list with all elements modified by the lambda.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`li = [22, 58, 66, 2, 4, 8, 9, 64]
final_list = list(map(lambda x: x * 2, li))
print(final_list)

# Output: [44, 116, 132, 4, 8, 16, 18, 128]`}
      </Box>

      {/* Lambda with Built-in: reduce */}
      <Typography
        component="h2"
        id="lambda-reduce"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        reduce() with Lambda
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>reduce()</code> function takes two arguments: a lambda function and a list. It applies the lambda repetitively over pairs of elements until a single result is achieved. Import from <code>functools</code>.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`from functools import reduce
li = [1, 2, 3, 4, 5, 6, 7]
sum = reduce((lambda x, y: x + y), li)
print(sum)

# Output: 28`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Example: Product of list elements</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`from functools import reduce
nums = [1, 2, 3, 4, 5]
product = reduce(lambda x, y: x * y, nums)
print(product)

# Output: 120`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('functions');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Functions
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('file-handling');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: File Handling →
        </Button>
      </Box>
    </motion.div>
  );
}

function FileHandlingTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        File Handling in Python
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 4 }}>
        Working with files in Python can be done by using the <code style={{ background: PRIMARY_LIGHT, padding: '2px 6px', borderRadius: 4 }}>open()</code> function. File handling is essential for reading data, writing outputs, and managing files and directories—skills used in data engineering, automation, and backend development at Reviving India.
      </Typography>

      {/* open() Syntax */}
      <Typography
        component="h2"
        id="file-open"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Syntax for open() Function
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`file_object = open(file_name [, access_mode][, buffering])`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        <strong>file_name</strong> — Name of the file.<br />
        <strong>access_mode</strong> — Determines the mode in which the file is opened.<br />
        <strong>buffering</strong> — 1 = line buffer; 0 = unbuffer; any positive = that buffer size; negative = system default.
      </Typography>

      {/* File Access Modes */}
      <Typography
        component="h2"
        id="file-modes"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        File Access Modes
      </Typography>
      <Box sx={{ overflow: 'auto', border: `1px solid ${BORDER}`, borderRadius: 2, mb: 2 }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor: PRIMARY_LIGHT }}>
              <Box component="th" sx={{ p: 1.5, textAlign: 'left', borderBottom: `2px solid ${BORDER}` }}>Mode</Box>
              <Box component="th" sx={{ p: 1.5, textAlign: 'left', borderBottom: `2px solid ${BORDER}` }}>Description</Box>
            </Box>
          </Box>
          <Box component="tbody" sx={{ '& tr': { borderBottom: `1px solid ${BORDER}` } }}>
            <Box component="tr"><Box component="td" sx={{ p: 1.5 }}><code>r</code></Box><Box component="td" sx={{ p: 1.5, color: TEXT_MUTED }}>Read (default)</Box></Box>
            <Box component="tr"><Box component="td" sx={{ p: 1.5 }}><code>w</code></Box><Box component="td" sx={{ p: 1.5, color: TEXT_MUTED }}>Write (overwrites)</Box></Box>
            <Box component="tr"><Box component="td" sx={{ p: 1.5 }}><code>a</code></Box><Box component="td" sx={{ p: 1.5, color: TEXT_MUTED }}>Append</Box></Box>
            <Box component="tr"><Box component="td" sx={{ p: 1.5 }}><code>x</code></Box><Box component="td" sx={{ p: 1.5, color: TEXT_MUTED }}>Create (error if exists)</Box></Box>
            <Box component="tr"><Box component="td" sx={{ p: 1.5 }}><code>r+</code></Box><Box component="td" sx={{ p: 1.5, color: TEXT_MUTED }}>Read and write</Box></Box>
          </Box>
        </Box>
      </Box>

      {/* How to Create a Text File */}
      <Typography
        component="h2"
        id="file-create"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        How to Create a Text File
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        To create a new file in Python, use the <code>open()</code> method with one of these parameters:
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        <li><strong style={{ color: TEXT_DARK }}>&quot;x&quot;</strong> — Create: creates a file, returns error if file exists</li>
        <li><strong style={{ color: TEXT_DARK }}>&quot;a&quot;</strong> — Append: creates file if it does not exist</li>
        <li><strong style={{ color: TEXT_DARK }}>&quot;w&quot;</strong> — Write: creates file if it does not exist (overwrites if exists)</li>
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`# x - Create: returns error if file exists
my_file = open("reviving_india_course.txt", "x")

# a - Append: creates file if it does not exist
my_file = open("reviving_india_log.txt", "a")

# w - Write: creates file if it does not exist
my_file = open("reviving_india_output.txt", "w")
my_file.close()`}
      </Box>

      {/* How to Read Files */}
      <Typography
        component="h2"
        id="file-read"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        How to Read Files
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        To read a file, use the <code>read()</code> function. By default it returns the whole text, but you can specify how many characters to return.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`my_file = open("revin.txt", "r")
print(my_file.read())

# Read specific number of characters
print(my_file.read(10))`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Read Lines</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use <code>readline()</code> to return one line, or <code>readlines()</code> to return a list of all lines. You can also loop over the file object to read the whole file.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Read one line
print(my_file.readline())

# Loop to read entire file line by line
my_file = open("reviving_india.txt", "r")
for x in my_file:
    print(x)
my_file.close()`}
      </Box>

      {/* close() Method */}
      <Typography
        component="h2"
        id="file-close"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        The close() Method
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>close()</code> method flushes any unwritten information and closes the file object. After closing, no more writing can be done.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`fo = open("foo.txt", "wb")
print("Name of the file:", fo.name)
fo.close()`}
      </Box>

      {/* write() Method */}
      <Typography
        component="h2"
        id="file-write"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        The write() Method
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>write()</code> method writes any string to an open file. Python strings can have binary data, not just text. <strong>Note:</strong> <code>write()</code> does not add a newline character (<code>\n</code>) automatically.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Syntax: fileObject.write(string)
x = open("revin.txt", "w")
x.write("Python is a great language.\\n")
x.write("Learn with Reviving India!\\n")
x.close()`}
      </Box>

      {/* File Positions */}
      <Typography
        component="h2"
        id="file-position"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        File Positions: tell() and seek()
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>tell()</code> returns the current position within the file. <code>seek(offset[, from])</code> changes the current file position. <strong>offset</strong> = number of bytes to move. <strong>from</strong>: 0 = beginning, 1 = current position, 2 = end of file.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`fo = open("foo.txt", "r+")
str = fo.read(10)
print("Read String is:", str)

# Check current position
position = fo.tell()
print("Current file position:", position)

# Reposition pointer at the beginning
position = fo.seek(0, 0)
str = fo.read(10)
print("Again read String is:", str)
fo.close()`}
      </Box>

      {/* with Statement */}
      <Typography
        component="h2"
        id="file-with"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Using with Statement (Best Practice)
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>with</code> statement ensures the file is properly closed when the block ends, even if an exception occurs. This is the recommended approach for file handling in Python.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# File automatically closed after block
with open("reviving_india.txt", "r") as my_file:
    content = my_file.read()
    print(content)
# No need to call close() - handled automatically`}
      </Box>

      {/* rename() Method */}
      <Typography
        component="h2"
        id="file-rename"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        The rename() Method
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Import the <code>os</code> module first. The <code>rename()</code> method takes two arguments: current filename and new filename.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Syntax: os.rename(current_file_name, new_file_name)
import os
os.rename("test1.txt", "test3.txt")`}
      </Box>

      {/* remove() Method */}
      <Typography
        component="h2"
        id="file-remove"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        The remove() Method
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use <code>remove()</code> to delete files by supplying the filename as the argument.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Syntax: os.remove(file_name)
import os
os.remove("test3.txt")`}
      </Box>

      {/* Directories */}
      <Typography
        component="h2"
        id="file-directories"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Directories: mkdir() and rmdir()
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>os</code> module has methods to create and remove directories.
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>mkdir() — Create directory</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`import os
os.mkdir("test")
# Creates a directory named "test" in current directory`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, mt: 2, fontSize: '0.95rem' }}>rmdir() — Remove directory</Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Before removing a directory, all contents in it must be removed first.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`import os
os.rmdir("test")
# Removes the empty directory "test"`}
      </Box>

      {/* path.exists and Path Checks */}
      <Typography
        component="h2"
        id="file-path-exists"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        path.exists() and Path Checks
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>path.exists()</code> returns <code>True</code> if the file or directory exists, else <code>False</code>. Use <code>os.path.isfile()</code> to check if a path is a file, and <code>os.path.isdir()</code> to check if it is a directory.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`import os.path
from os import path

# Check if file or directory exists
print(path.exists("guru99.txt"))
print(path.exists("test"))

# Check if path is a file
print(os.path.isfile("foo.txt"))

# Check if path is a directory
print(os.path.isdir("test"))`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Real-world Example: Reviving India Data Log</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Example: Append log entries with timestamp
import os
from datetime import datetime

log_file = "reviving_india_activity.log"
if not os.path.exists(log_file):
    open(log_file, "x").close()

with open(log_file, "a") as f:
    timestamp = datetime.now().strftime("%Y-%m-%d %H:%M")
    f.write(f"[{timestamp}] Course progress updated\\n")
    print("Log entry added successfully")`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('functions');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Functions
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('iterators');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Iterators →
        </Button>
      </Box>
    </motion.div>
  );
}

function IteratorsTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        Iterators in Python
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 4 }}>
        An <strong>iterator</strong> is a Python object used to iterate over iterable objects like lists, tuples, dicts, sets, and strings. Iterators power the <code>for</code> loop and are essential for lazy evaluation and memory-efficient data processing—commonly used in Reviving India&apos;s data pipelines and backend systems.
      </Typography>

      {/* What is an Iterator */}
      <Typography
        component="h2"
        id="iter-intro"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        What is an Iterator?
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        An iterator is an object that implements the iterator protocol. It consists of two methods: <code>__iter__()</code> and <code>__next__()</code>. Any object that can be looped over (list, tuple, string, dict, set) is <strong>iterable</strong>; an <strong>iterator</strong> is the object that produces values one at a time.
      </Typography>

      {/* __iter__ and __next__ */}
      <Typography
        component="h2"
        id="iter-methods"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        __iter__() and __next__()
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>__iter__()</code> — Called for initialization of the iterator. It returns the iterator object itself.<br />
        <code>__next__()</code> — Returns the next value in the sequence. When there are no more items, it raises <code>StopIteration</code>.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        The <code>for</code> loop internally creates an iterator object and calls <code>next()</code> for each iteration.
      </Typography>

      {/* iter() and next() Built-in */}
      <Typography
        component="h2"
        id="iter-builtin"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        iter() and next() Built-in Functions
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use <code>iter(iterable)</code> to get an iterator from any iterable. Use <code>next(iterator)</code> to get the next value.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`mytup = ("revin", "reviving", "india")
myit = iter(mytup)

print(next(myit))  # Output: revin
print(next(myit))  # Output: reviving
print(next(myit))  # Output: india
# print(next(myit))  # Raises StopIteration`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Example: Iterator over string</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`channel = "Reviving India"
it = iter(channel)
print(next(it))  # R
print(next(it))  # e
print(next(it))  # v`}
      </Box>

      {/* Iterator with for loop */}
      <Typography
        component="h2"
        id="iter-loop"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        How the for Loop Uses Iterators
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        When you write <code>for x in mylist:</code>, Python calls <code>iter(mylist)</code> to get an iterator, then repeatedly calls <code>next()</code> until <code>StopIteration</code> is raised.
      </Typography>

      {/* Manual Iteration Pattern */}
      <Typography
        component="h2"
        id="iter-manual"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Manual Iteration Pattern
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        You can manually iterate using a <code>while</code> loop and handle <code>StopIteration</code>:
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`iter_obj = iter(iterable)
while True:
    try:
        element = next(iter_obj)
        # Process element
    except StopIteration:
        break`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`courses = ["Python", "Data Engineering", "Reviving India"]
it = iter(courses)
while True:
    try:
        course = next(it)
        print(course)
    except StopIteration:
        break

# Output: Python, Data Engineering, Reviving India`}
      </Box>

      {/* Create Custom Iterator */}
      <Typography
        component="h2"
        id="iter-create"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Create a Custom Iterator
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Implement <code>__iter__()</code> and <code>__next__()</code> in your class. <code>__iter__()</code> does initialization and returns the iterator object. <code>__next__()</code> returns the next item in the sequence.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`class MyNum:
    def __iter__(self):
        self.a = 1
        return self

    def __next__(self):
        x = self.a
        self.a += 1
        return x

obj1 = MyNum()
myiter = iter(obj1)
print(next(myiter))  # 1
print(next(myiter))  # 2
print(next(myiter))  # 3`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2, fontStyle: 'italic' }}>
        Without <code>StopIteration</code>, this iterator would continue forever if you had enough <code>next()</code> calls.
      </Typography>

      {/* StopIteration */}
      <Typography
        component="h2"
        id="iter-stop"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        StopIteration
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        To prevent infinite iteration, raise <code>StopIteration</code> when there are no more items to return.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`class CountDown:
    def __init__(self, start):
        self.current = start

    def __iter__(self):
        return self

    def __next__(self):
        if self.current <= 0:
            raise StopIteration
        self.current -= 1
        return self.current + 1

for num in CountDown(3):
    print(num)
# Output: 3, 2, 1`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Real-world Example: Reviving India course modules iterator</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`class CourseModules:
    def __init__(self, modules):
        self.modules = modules
        self.index = 0

    def __iter__(self):
        return self

    def __next__(self):
        if self.index >= len(self.modules):
            raise StopIteration
        module = self.modules[self.index]
        self.index += 1
        return module

reviving_india_modules = CourseModules(["Python Basics", "Functions", "File Handling", "OOP"])
for m in reviving_india_modules:
    print(m)
# Output: Python Basics, Functions, File Handling, OOP`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('file-handling');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: File Handling
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('generators');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Generators →
        </Button>
      </Box>
    </motion.div>
  );
}

function GeneratorsTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        Generators in Python
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 4 }}>
        Python <strong>generators</strong> are a simple way of creating iterators. A generator is a function that returns an iterator which you can iterate over—one value at a time. If the body of a <code>def</code> contains <code>yield</code>, the function automatically becomes a generator function. Generators are memory-efficient and widely used in data streaming, Reviving India pipelines, and large dataset processing.
      </Typography>

      {/* What are Generators */}
      <Typography
        component="h2"
        id="gen-intro"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        What are Generators?
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        A generator function returns a generator object (iterator) that produces values lazily—one at a time—instead of building and returning an entire list. This makes generators ideal for large data or infinite sequences.
      </Typography>

      {/* Why Generators */}
      <Typography
        component="h2"
        id="gen-why"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Why Generators?
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        To build an iterator with a class, you must implement <code>__iter__()</code> and <code>__next__()</code>, track internal state, and raise <code>StopIteration</code> when done. This can be lengthy and counterintuitive.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        <strong>Generators</strong> let you write iterator logic with a simple function and <code>yield</code>—no class boilerplate required.
      </Typography>

      {/* Create Generators */}
      <Typography
        component="h2"
        id="gen-create"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Create Generators
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Define a normal function and use <code>yield</code> instead of <code>return</code>. Each <code>yield</code> produces a value and pauses the function; it resumes on the next <code>next()</code> call.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def GeneratorFun():
    yield 1
    yield 2
    yield 3

for var in GeneratorFun():
    print(var)

# Output: 1, 2, 3`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`# Using next() explicitly
gen = GeneratorFun()
print(next(gen))  # 1
print(next(gen))  # 2
print(next(gen))  # 3
# print(next(gen))  # Raises StopIteration`}
      </Box>

      {/* yield vs return */}
      <Typography
        component="h2"
        id="gen-yield-return"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        yield vs return
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>return</code> terminates the function entirely and returns a value. <code>yield</code> pauses the function, saves all its state, and continues from there on successive calls. The generator retains its local variables between yields.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def count_up_to(n):
    count = 0
    while count < n:
        yield count
        count += 1

for i in count_up_to(4):
    print(i)
# Output: 0, 1, 2, 3`}
      </Box>

      {/* Generator Expressions */}
      <Typography
        component="h2"
        id="gen-expression"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Generator Expressions
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Similar to list comprehensions, but with parentheses instead of square brackets. Generator expressions create a generator object—they do not build the full list in memory.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Initialize the list
my_list = [1, 3, 6, 10]

# Generator expression (parentheses, not brackets)
a = (x**2 for x in my_list)

print(next(a))  # 1
print(next(a))  # 9
print(next(a))  # 36
print(next(a))  # 100
# next(a)  # Raises StopIteration`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>List comprehension vs generator expression</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# List: builds entire list in memory
squares_list = [x**2 for x in range(1000000)]

# Generator: produces values on demand
squares_gen = (x**2 for x in range(1000000))
# squares_gen is memory-efficient`}
      </Box>

      {/* Lazy Evaluation */}
      <Typography
        component="h2"
        id="gen-lazy"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Lazy Evaluation
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Generators produce values only when requested. They don&apos;t compute or store the entire sequence upfront—ideal for large datasets, infinite sequences, or streaming data.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def reviving_india_courses():
    """Generator yielding course names one at a time."""
    courses = ["Python Basics", "Functions", "File Handling", "Iterators", "Generators", "OOP"]
    for course in courses:
        yield course

for c in reviving_india_courses():
    print(f"Learning: {c}")

# Output: Learning: Python Basics, Learning: Functions, ...`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Example: Infinite sequence</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def infinite_count(start=0):
    n = start
    while True:
        yield n
        n += 1

# Take first 5 values
gen = infinite_count(10)
for _ in range(5):
    print(next(gen))
# Output: 10, 11, 12, 13, 14`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Example: Process large file line by line</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def read_large_file(filename):
    """Yield lines one at a time instead of loading entire file."""
    with open(filename, "r") as f:
        for line in f:
            yield line.strip()

# Memory-efficient: processes one line at a time
for line in read_large_file("reviving_india_log.txt"):
    print(line)`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('iterators');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Iterators
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('try-except-finally');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Try Exception Finally →
        </Button>
      </Box>
    </motion.div>
  );
}

function TryExceptFinallyTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        Try, Except, and Finally
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 4 }}>
        The <code>try</code> and <code>except</code> statement is used to handle errors within your code in Python. Exception handling prevents your program from crashing when unexpected errors occur—essential for robust applications, APIs, and data pipelines at Reviving India.
      </Typography>

      {/* Syntax */}
      <Typography
        component="h2"
        id="try-syntax"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Syntax
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`try:
    # Some Code
except:
    # Executed if error in the try block
else:
    # Optional: executed if no error
finally:
    # Always executed`}
      </Box>

      {/* Try and Except */}
      <Typography
        component="h2"
        id="try-intro"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Try and Except
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>try</code> block lets you test a block of code for errors. The <code>except</code> block lets you handle the error. The <code>finally</code> block lets you execute code regardless of the result of the try and except blocks.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`try:
    print(x)
except:
    print("An exception occurred")

# Output: An exception occurred
# (x is not defined - NameError)`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        Without <code>try/except</code>, <code>print(x)</code> would raise a <code>NameError</code> and stop the program. With exception handling, we catch and handle it gracefully.
      </Typography>

      {/* What are Exceptions */}
      <Typography
        component="h2"
        id="try-exceptions"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        What are Exceptions?
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        If an error occurs while running the program, it&apos;s called an <strong>exception</strong>. When an exception occurs, Python normally stops and generates an error message. Common exceptions include:
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        <li><code>FileNotFoundError</code> — File not found</li>
        <li><code>ZeroDivisionError</code> — Division by zero</li>
        <li><code>ImportError</code> — Module import failed</li>
        <li><code>KeyError</code> — Key not in dictionary</li>
        <li><code>IndexError</code> — Index out of range</li>
        <li><code>TypeError</code> — Wrong type of operand</li>
        <li><code>ValueError</code> — Invalid value</li>
        <li><code>SyntaxError</code> — Invalid syntax</li>
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        These exceptions can be handled using the <code>try</code> statement so your program continues running instead of crashing.
      </Typography>

      {/* Else Block */}
      <Typography
        component="h2"
        id="try-else"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Else Block
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use the <code>else</code> keyword to define a block of code executed only if no errors were raised in the <code>try</code> block.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`try:
    print("Hello")
except:
    print("Something went wrong")
else:
    print("Nothing went wrong")

# Output: Hello
#         Nothing went wrong`}
      </Box>

      {/* Finally Block */}
      <Typography
        component="h2"
        id="try-finally"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Finally Block
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        The <code>finally</code> block, if specified, is always executed—whether the try block raises an error or not. Use it for cleanup (e.g., closing files, releasing resources).
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`try:
    print(x)
except:
    print("Something went wrong")
finally:
    print("The 'try except' is finished")

# Output: Something went wrong
#         The 'try except' is finished`}
      </Box>

      {/* Multiple Except Blocks */}
      <Typography
        component="h2"
        id="try-multiple"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Multiple Except Blocks
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        You can catch specific exception types with multiple <code>except</code> blocks. This lets you handle each error type differently.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`try:
    lunch()  # Some code that may raise exceptions
except SyntaxError:
    print("Fix your syntax")
except TypeError:
    print("Oh no! A TypeError has occurred")
except ValueError:
    print("A ValueError occurred!")
except ZeroDivisionError:
    print("Divide by zero?")
else:
    print("No exception")
finally:
    print("Ok then")`}
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Example: Handling different numeric errors</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def divide(a, b):
    try:
        result = a / b
        print(f"Result: {result}")
    except ZeroDivisionError:
        print("Cannot divide by zero!")
    except TypeError:
        print("Arguments must be numbers")
    else:
        print("Division completed successfully")
    finally:
        print("Operation finished")

divide(10, 2)   # Result: 5.0, Division completed..., Operation finished
divide(10, 0)   # Cannot divide by zero!, Operation finished`}
      </Box>

      {/* raise Statement */}
      <Typography
        component="h2"
        id="try-raise"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        raise Statement
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use the <code>raise</code> statement to intentionally raise an exception. You can raise built-in or custom exceptions.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`# Raise a built-in exception
x = -1
if x < 0:
    raise ValueError("Number must be positive")

# Raise with custom message
def get_course(course_id):
    courses = {"1": "Python", "2": "Reviving India"}
    if course_id not in courses:
        raise KeyError(f"Course {course_id} not found")
    return courses[course_id]`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Real-world Example: Safe file read with Reviving India</Typography>
      <Box component="pre" sx={codeBlockSx}>
{`def read_config(filename):
    try:
        with open(filename, "r") as f:
            return f.read()
    except FileNotFoundError:
        print(f"Config file '{filename}' not found. Using defaults.")
        return "default_config"
    except PermissionError:
        print("Permission denied to read file.")
        raise
    finally:
        print("Config load attempt completed.")

# Safe usage
config = read_config("reviving_india_config.txt")
print(config)`}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('generators');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Generators
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('python-json');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Python JSON →
        </Button>
      </Box>
    </motion.div>
  );
}

function PythonJsonTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        Python JSON
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 4 }}>
        JSON (JavaScript Object Notation) is a text format for storing and exchanging data. In JSON, data is represented as quoted-strings with key-value pairs enclosed in curly brackets <code>{'{}'}</code>. Python has a built-in <code>json</code> package to work with JSON data—essential for APIs, config files, and data pipelines at Reviving India.
      </Typography>

      {/* What is JSON */}
      <Typography
        component="h2"
        id="json-intro"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        What is JSON?
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        JSON is text written with JavaScript object notation. It uses key-value pairs, arrays, and nested structures. Python&apos;s <code>json</code> module converts between JSON strings and Python objects (dict, list, str, int, float, bool, None).
      </Typography>

      {/* json.loads() */}
      <Typography
        component="h2"
        id="json-loads"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        json.loads()
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>json.loads()</code> parses a valid JSON string and converts it into a Python dictionary. Syntax: <code>json.loads(s)</code>
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`import json

# JSON string
emp = '{"id": "09", "name": "Praveen", "department": "IT"}'

# Convert string to Python dict
emp_dict = json.loads(emp)
print(emp_dict)
# Output: {'id': '09', 'name': 'Praveen', 'department': 'IT'}

print(emp_dict['name'])
# Output: Praveen`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`# Reviving India course data
course_json = '{"course": "Python", "platform": "Reviving India", "modules": 6}'
course_dict = json.loads(course_json)
print(course_dict["platform"])
# Output: Reviving India`}
      </Box>

      {/* json.load() */}
      <Typography
        component="h2"
        id="json-load"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        json.load()
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>json.load()</code> takes a file object and returns the parsed JSON as a Python object. Syntax: <code>json.load(file_object)</code>
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`import json

f = open('data.json')
data = json.load(f)
f.close()

print(data)`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`# Better: use with statement
with open('reviving_india_courses.json') as f:
    courses = json.load(f)
    print(courses)`}
      </Box>

      {/* json.dumps() */}
      <Typography
        component="h2"
        id="json-dumps"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        json.dumps()
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>json.dumps()</code> converts a Python object into a JSON string. Python types map to JSON: dict→object, list→array, str→string, int/float→number, True/False→true/false, None→null.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`import json

x = {
    "name": "revin",
    "country": "India"
}

# Convert into JSON string
y = json.dumps(x)

print(y)
# Output: {"name": "revin", "country": "India"}`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`# Reviving India config
config = {"platform": "Reviving India", "course": "Python", "active": True}
json_str = json.dumps(config)
print(json_str)
# Output: {"platform": "Reviving India", "course": "Python", "active": true}`}
      </Box>

      {/* json.dump() */}
      <Typography
        component="h2"
        id="json-dump"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        json.dump()
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <code>json.dump()</code> writes a Python object to a JSON file. Use it when you need to store data in a file.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`import json

x = {
    "name": "revin",
    "country": "India"
}

out_file = open("myfile.json", "w")
json.dump(x, out_file, indent=4)
out_file.close()`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`# Save Reviving India user progress
user_progress = {
    "user": "learner",
    "platform": "Reviving India",
    "completed_modules": ["Python Basics", "Functions", "File Handling"]
}
with open("reviving_india_progress.json", "w") as f:
    json.dump(user_progress, f, indent=2)`}
      </Box>

      {/* Pretty Print JSON */}
      <Typography
        component="h2"
        id="json-pretty"
        sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, mb: 2, mt: 4 }}
      >
        Pretty Print JSON
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        Use <code>indent</code> and <code>sort_keys</code> with <code>json.dumps()</code> or <code>json.dump()</code> for readable output. <code>indent</code> adds whitespace; <code>sort_keys=True</code> sorts keys alphabetically.
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`import json

x = '{"name": "revin", "languages": "Python", "numbers": [2, 1.6, null]}'
x_dict = json.loads(x)

print(json.dumps(x_dict, indent=4, sort_keys=True))

# Output (formatted):
# {
#     "languages": "Python",
#     "name": "revin",
#     "numbers": [2, 1.6, null]
# }`}
      </Box>
      <Box component="pre" sx={codeBlockSx}>
{`# Pretty save for config
reviving_india_config = {
    "api_url": "https://revivingindia.com",
    "courses": ["Python", "Data Engineering"],
    "version": 1
}
with open("reviving_india_config.json", "w") as f:
    json.dump(reviving_india_config, f, indent=4, sort_keys=True)`}
      </Box>

      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, mt: 2, fontSize: '0.95rem' }}>Common parameters for json.dumps() and json.dump()</Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        <li><code>indent</code> — Number of spaces for indentation (e.g., 2 or 4)</li>
        <li><code>sort_keys</code> — Sort keys alphabetically when True</li>
        <li><code>ensure_ascii</code> — False to allow non-ASCII characters (e.g., Indian languages)</li>
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('try-except-finally');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Try Exception Finally
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('oop');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: OOP (Classes & Objects) →
        </Button>
      </Box>
    </motion.div>
  );
}

const OOP_TOPICS: { id: string; label: string }[] = [
  { id: 'oop-class', label: 'Class' },
  { id: 'oop-constructor', label: 'Constructor' },
  { id: 'oop-inheritance', label: 'Inheritance' },
  { id: 'oop-polymorphism', label: 'Polymorphism' },
  { id: 'oop-abstraction', label: 'Abstraction' },
  { id: 'oop-encapsulation', label: 'Encapsulation' },
  { id: 'oop-abstract-class', label: 'Abstract Class' },
];

function OOPLandingView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        OOP (Classes & Objects)
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 4 }}>
        Object-Oriented Programming (OOP) uses classes and objects to organize code around data and behavior. Python supports the core OOP principles. Select a topic below to learn more.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {OOP_TOPICS.map((t) => (
          <Button
            key={t.id}
            variant="outlined"
            onClick={() => {
              onNavigate(t.id);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            sx={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              borderColor: BORDER,
              color: TEXT_DARK,
              py: 1.5,
              px: 2,
              '&:hover': { borderColor: PRIMARY, bgcolor: PRIMARY_LIGHT },
            }}
          >
            {t.label}
          </Button>
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate('python-json');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: Python JSON
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate('oop-class');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: Class →
        </Button>
      </Box>
    </motion.div>
  );
}

const OOP_SUB_CONTENT: Record<
  string,
  { title: string; intro: string; code: string; prevId: string; prevLabel: string; nextId: string; nextLabel: string }
> = {
  'oop-class': {
    title: 'Class',
    intro: '',
    code: '',
    prevId: 'python-json',
    prevLabel: 'Python JSON',
    nextId: 'oop-constructor',
    nextLabel: 'Constructor',
  },
  'oop-constructor': {
    title: 'Constructor',
    intro: 'The constructor is a special method __init__ that runs when an object is created. It initializes instance attributes.',
    code: `class Employee:
    def __init__(self, name, dept):
        self.name = name
        self.dept = dept

emp = Employee("Amit", "IT")
print(emp.name, emp.dept)
# Output: Amit IT`,
    prevId: 'oop-class',
    prevLabel: 'Class',
    nextId: 'oop-inheritance',
    nextLabel: 'Inheritance',
  },
  'oop-inheritance': {
    title: 'Inheritance',
    intro: 'Inheritance lets a child class inherit attributes and methods from a parent class. Use the parent class name in parentheses when defining the child.',
    code: `class Animal:
    def speak(self):
        print("Some sound")

class Dog(Animal):
    def speak(self):
        print("Woof!")

d = Dog()
d.speak()
# Output: Woof!`,
    prevId: 'oop-constructor',
    prevLabel: 'Constructor',
    nextId: 'oop-polymorphism',
    nextLabel: 'Polymorphism',
  },
  'oop-polymorphism': {
    title: 'Polymorphism',
    intro: 'Polymorphism allows objects of different classes to be used through a common interface. The same method name can have different implementations in different classes.',
    code: `class Animal:
    def speak(self):
        print("Some sound")

class Dog(Animal):
    def speak(self):
        print("Woof!")

class Cat(Animal):
    def speak(self):
        print("Meow")

def make_speak(animal):
    animal.speak()

make_speak(Dog())
make_speak(Cat())
# Output: Woof!
# Output: Meow`,
    prevId: 'oop-inheritance',
    prevLabel: 'Inheritance',
    nextId: 'oop-abstraction',
    nextLabel: 'Abstraction',
  },
  'oop-abstraction': {
    title: 'Abstraction',
    intro: 'Abstraction hides complex implementation details and exposes only what is necessary. It helps reduce complexity and improve maintainability.',
    code: `# User calls simple methods without knowing internal logic
class Database:
    def connect(self):
        # complex connection logic hidden
        return True

db = Database()
db.connect()
# User doesn't need to know how connect() works`,
    prevId: 'oop-polymorphism',
    prevLabel: 'Polymorphism',
    nextId: 'oop-encapsulation',
    nextLabel: 'Encapsulation',
  },
  'oop-encapsulation': {
    title: 'Encapsulation',
    intro: 'Encapsulation bundles data and methods that operate on that data, and restricts direct access using private attributes (prefix with _ or __).',
    code: `class BankAccount:
    def __init__(self, balance):
        self.__balance = balance  # private

    def deposit(self, amount):
        self.__balance += amount

    def get_balance(self):
        return self.__balance

acc = BankAccount(1000)
acc.deposit(500)
print(acc.get_balance())
# Output: 1500`,
    prevId: 'oop-abstraction',
    prevLabel: 'Abstraction',
    nextId: 'oop-abstract-class',
    nextLabel: 'Abstract Class',
  },
  'oop-abstract-class': {
    title: 'Abstract Class',
    intro: 'An abstract class cannot be instantiated directly. It defines abstract methods that subclasses must implement. Use ABC from the abc module.',
    code: `from abc import ABC, abstractmethod

class Shape(ABC):
    @abstractmethod
    def area(self):
        pass

class Rectangle(Shape):
    def __init__(self, w, h):
        self.w = w
        self.h = h
    def area(self):
        return self.w * self.h

r = Rectangle(4, 5)
print(r.area())
# Output: 20`,
    prevId: 'oop-encapsulation',
    prevLabel: 'Encapsulation',
    nextId: 'comprehensions',
    nextLabel: 'Comprehensions',
  },
};

function ClassTopicView({ onNavigate }: { onNavigate: (topicId: string) => void }) {
  const c = OOP_SUB_CONTENT['oop-class'];
  const contentId = 'oop-class-content';
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        id={contentId}
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        Class
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, fontSize: '0.95rem' }}>
        What is a Class?
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        <li>It is a collection of objects.</li>
        <li>It is a group of attributes &amp; methods.</li>
        <li>Class is a model which consists of class variable &amp; class methods.</li>
        <li>Classes are created by &quot;class&quot; keyword.</li>
      </Box>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, fontSize: '0.95rem' }}>
        Attribute &amp; method
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <strong>Attribute</strong> is just a variable which contain data.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        <strong>Method</strong> is similar to function which perform certain task or logic.
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, fontSize: '0.95rem' }}>
        Syntax
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`class Class_name:
    x = 10
    def __init__(self):
        self.VM = value

    def method_name(self):
        # statements`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1, textAlign: 'center' }}>
        or
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`class Class_name(object):
    def __init__(self):
        self.VM = value

    def method_name(self):
        pass`}
      </Box>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2, fontSize: '0.9rem' }}>
        <strong>Object</strong> represents the base class name from where all classes in Python are derived. This is optional.
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, fontSize: '0.95rem' }}>
        __init__ &amp; Self
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        <strong>__init__</strong> method is known as a constructor in class.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        This method is used to initialize the instance variables.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 1 }}>
        It is called automatically when an object of class is created; we don&apos;t call this method explicitly.
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>
        <strong>Self</strong> is param or variable which refers to current class object/instance.
      </Typography>
      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 1, fontSize: '0.95rem' }}>
        Empty Class
      </Typography>
      <Box component="pre" sx={codeBlockSx}>
{`class RevivingIndia:
    pass`}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate(c.prevId);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: {c.prevLabel}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate(c.nextId);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: {c.nextLabel} →
        </Button>
      </Box>
    </motion.div>
  );
}

function OOPSubTopicView({ topicId, onNavigate }: { topicId: string; onNavigate: (topicId: string) => void }) {
  const c = OOP_SUB_CONTENT[topicId];
  if (!c) return null;
  if (topicId === 'oop-class') {
    return <ClassTopicView onNavigate={onNavigate} />;
  }
  const contentId = `${topicId}-content`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        id={contentId}
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 3,
        }}
      >
        {c.title}
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85, mb: 2 }}>{c.intro}</Typography>
      <Box component="pre" sx={codeBlockSx}>
        {c.code}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onNavigate(c.prevId);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_HOVER },
          }}
        >
          ← Previous: {c.prevLabel}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onNavigate(c.nextId);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 600,
            borderRadius: '12px',
            px: 3,
            py: 1,
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
            '&:hover': { bgcolor: PRIMARY_HOVER },
          }}
        >
          Next: {c.nextLabel} →
        </Button>
      </Box>
    </motion.div>
  );
}

const DATATYPE_TOPIC_INFO: Record<string, { title: string; intro: string }> = {
  number: {
    title: 'Number',
    intro: 'Python supports integers (int), floating-point numbers (float), and complex numbers (complex). Numbers are immutable. Example: x = 10, price = 99.5, z = 3+4j.',
  },
  string: {
    title: 'String',
    intro: 'Strings are sequences of characters enclosed in single, double, or triple quotes. They are immutable. Example: name = "Reviving India", line = \'Hello\'.',
  },
  tuple: {
    title: 'Tuple',
    intro: 'Tuples are ordered, immutable sequences. They use parentheses. Once created, elements cannot be added, removed, or changed. Example: t = (1, 2, 3).',
  },
  list: {
    title: 'List',
    intro: 'Lists are ordered, mutable sequences. They use square brackets. You can add, remove, and modify elements. Example: nums = [10, 20, 30].',
  },
  dictionary: {
    title: 'Dictionary',
    intro: 'Dictionaries store key-value pairs. They are mutable and unordered (before Python 3.7). Use curly braces. Example: d = {"name": "Amit", "age": 25}.',
  },
  sets: {
    title: 'Sets',
    intro: 'Sets are unordered collections of unique elements. They are mutable—you can add and remove items. Duplicates are not allowed. Example: s = {1, 2, 3}.',
  },
};

function DatatypeTopicView({ topicId }: { topicId: string }) {
  const info = DATATYPE_TOPIC_INFO[topicId];
  if (!info) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        {info.title}
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.85 }}>
        {info.intro}
      </Typography>
    </motion.div>
  );
}

function SectionLandingView({
  content,
  onStartWithTopic,
  progress,
  activeId,
}: {
  content: SectionLandingContent;
  onStartWithTopic: (topicId: string) => void;
  progress: number;
  activeId: string | null;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        {content.title}
      </Typography>

      <Typography
        sx={{
          color: TEXT_MUTED,
          lineHeight: 1.8,
          mb: 3,
          fontSize: '1rem',
        }}
      >
        {content.intro}
      </Typography>

      <Typography
        component="h2"
        sx={{
          fontSize: '1.2rem',
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 1.5,
        }}
      >
        What You Will Learn
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 3, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        {content.whatYouWillLearn.map((item, i) => (
          <motion.li
            key={item}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05 * i }}
          >
            {item}
          </motion.li>
        ))}
      </Box>

      <Typography
        component="h2"
        sx={{
          fontSize: '1.2rem',
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 1.5,
        }}
      >
        Why This Section Matters
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.8, mb: 3 }}>
        {content.whyItMatters}
      </Typography>

      <Typography sx={{ color: TEXT_DARK, fontWeight: 500, mb: 2 }}>
        {content.actionPrompt}
      </Typography>

      {content.startWithTopicId && (
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ display: 'inline-block' }}>
          <Button
            variant="contained"
            endIcon={<ArrowRight size={18} />}
            onClick={() => onStartWithTopic(content.startWithTopicId!)}
            sx={{
              bgcolor: PRIMARY,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '12px',
              px: 2.5,
              py: 1.25,
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
              '&:hover': { bgcolor: PRIMARY_HOVER },
            }}
          >
            {content.startWithButtonText ?? `Start with: ${content.startWithLabel} →`}
          </Button>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        style={{ marginTop: 32 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            borderRadius: '12px',
            bgcolor: PRIMARY_LIGHT,
            border: `1px solid ${BORDER}`,
          }}
        >
          <Lightbulb size={22} color={PRIMARY} style={{ flexShrink: 0, marginTop: 2 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>
              Tip
            </Typography>
            <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.7, fontSize: '0.9rem' }}>
              {content.tip}
            </Typography>
          </Box>
        </Box>
      </motion.div>

      <Box sx={{ mt: 3 }}>
        <Box sx={{ height: 8, bgcolor: BORDER, borderRadius: 4, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{ height: '100%', backgroundColor: PRIMARY, borderRadius: 4 }}
          />
        </Box>
      </Box>
    </motion.div>
  );
}

const PythonCourse: React.FC = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string>('fundamentals');
  const [expandedIds] = useState<Set<string>>(
    () => new Set(SIDEBAR_SECTIONS.map((s) => s.id))
  );
  const [expandedSubIds] = useState<Set<string>>(() => new Set(['variables-datatypes', 'conditions-loops', 'oop']));
  const [progress] = useState(20);

  const selectSection = (id: string) => {
    setSelectedSectionId(id);
    setActiveId(null);
  };

  const handleSectionClick = (id: string) => {
    selectSection(id);
  };

  const handleTopicClick = (topicId: string, sectionId: string) => {
    setActiveId(topicId);
    setSelectedSectionId(sectionId);
  };

  const sidebarSx = {
    width: 280,
    flexShrink: 0,
    bgcolor: CARD_BG,
    borderRight: `1px solid ${BORDER}`,
    position: 'sticky' as const,
    top: 56,
    height: 'calc(100vh - 56px)',
    overflowY: 'auto' as const,
    py: 2.5,
    px: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    zIndex: 10,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
        pt: { xs: 2, md: 8 },
        pb: 2,
        bgcolor: BG,
        fontFamily: '"Inter", "Poppins", sans-serif',
        overflowX: 'hidden',
      }}
    >
      {/* Mobile: header + collapsible menu + content below */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${BORDER}`,
            bgcolor: CARD_BG,
            position: 'sticky',
            top: 0,
            zIndex: 11,
          }}
        >
          <Button
            startIcon={<ChevronLeft size={18} />}
            onClick={() => navigate('/')}
            size="small"
            sx={{ textTransform: 'none', fontWeight: 600, color: TEXT_DARK }}
          >
            Home
          </Button>
        </Box>
        {/* Mobile: menu in scrollable strip so content is visible below */}
        <Box
          sx={{
            width: '100%',
            bgcolor: CARD_BG,
            borderBottom: `1px solid ${BORDER}`,
            py: 2,
            px: 2,
            overflowX: 'auto',
            maxHeight: { xs: '45vh', sm: '50vh' },
            overflowY: 'auto',
          }}
        >
          <Typography component="h2" sx={{ fontFamily: MENU_TITLE_FONT, fontSize: '1.2rem', fontWeight: 800, mb: 2, px: 1, color: TEXT_DARK }}>
            Python Learning Path
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {SIDEBAR_SECTIONS.map((section) => {
              const isExpanded = expandedIds.has(section.id);
              return (
                <li key={section.id} style={{ marginBottom: 6 }}>
                  <Box
                    onClick={() => handleSectionClick(section.id)}
                    sx={{
                      py: 1.25,
                      px: 2,
                      cursor: 'pointer',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      fontFamily: MENU_SECTION_FONT,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: isExpanded ? PRIMARY : TEXT_DARK,
                      bgcolor: isExpanded ? PRIMARY_LIGHT : 'transparent',
                      borderLeft: `3px solid ${isExpanded ? PRIMARY : 'transparent'}`,
                    }}
                  >
                    {isExpanded ? <ChevronDown size={20} strokeWidth={2.5} color={PRIMARY} /> : <ChevronRight size={20} strokeWidth={2.5} color={TEXT_MUTED} />}
                    {section.label}
                  </Box>
                  {isExpanded && (
                    <Box sx={{ pl: 3.5, pr: 1, py: 0.75, borderLeft: `2px solid ${BORDER}`, ml: 1.5 }}>
                      {section.children.map((child) => {
                        const hasSubMenu = child.children && child.children.length > 0;
                        const isSubExpanded = hasSubMenu && expandedSubIds.has(child.id);
                        const isSubActive = hasSubMenu && child.children!.some((s) => s.id === activeId);
                        const isParentActive = activeId === child.id || isSubActive;
                        return (
                          <Box key={child.id}>
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTopicClick(child.id, section.id);
                              }}
                              sx={{
                                py: 1.1,
                                px: 2,
                                cursor: 'pointer',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                fontFamily: SUBMENU_FONT,
                                fontSize: '0.925rem',
                                fontWeight: isParentActive ? 700 : 500,
                                bgcolor: isParentActive ? PRIMARY_LIGHT : 'transparent',
                                color: isParentActive ? PRIMARY : '#475569',
                                borderLeft: isParentActive ? `3px solid ${PRIMARY}` : '3px solid transparent',
                              }}
                            >
                              {hasSubMenu && (isSubExpanded ? <ChevronDown size={16} strokeWidth={2.5} color={isParentActive ? PRIMARY : TEXT_MUTED} /> : <ChevronRight size={16} strokeWidth={2.5} color={TEXT_MUTED} />)}
                              {child.label}
                            </Box>
                            {hasSubMenu && isSubExpanded && (
                              <Box sx={{ pl: 2.5, pr: 1, py: 0.5, borderLeft: `1px solid ${BORDER}`, ml: 2 }}>
                                {child.children!.map((sub) => (
                                  <Box
                                    key={sub.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTopicClick(sub.id, section.id);
                                    }}
                                    sx={{
                                      py: 1,
                                      px: 1.5,
                                      cursor: 'pointer',
                                      borderRadius: '8px',
                                      fontFamily: SUBMENU_FONT,
                                      fontSize: '0.875rem',
                                      fontWeight: activeId === sub.id ? 700 : 500,
                                      bgcolor: activeId === sub.id ? PRIMARY_LIGHT : 'transparent',
                                      color: activeId === sub.id ? PRIMARY : '#475569',
                                      borderLeft: activeId === sub.id ? `3px solid ${PRIMARY}` : '3px solid transparent',
                                    }}
                                  >
                                    {sub.label}
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </li>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Sidebar - desktop only */}
      <Box
        sx={{
          ...sidebarSx,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: MENU_TITLE_FONT,
            fontSize: '1.35rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            background: `linear-gradient(135deg, ${TEXT_DARK} 0%, ${PRIMARY} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 2.5,
            px: 1,
            lineHeight: 1.2,
          }}
        >
          Python Learning Path
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {SIDEBAR_SECTIONS.map((section, sectionIndex) => {
            const isExpanded = expandedIds.has(section.id);
            return (
              <motion.li
                key={section.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: sectionIndex * 0.04 }}
                style={{ marginBottom: 6 }}
              >
                <Box
                  onClick={() => handleSectionClick(section.id)}
                  sx={{
                    py: 1.25,
                    px: 2,
                    cursor: 'pointer',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    fontFamily: MENU_SECTION_FONT,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    letterSpacing: '0.01em',
                    color: isExpanded ? PRIMARY : TEXT_DARK,
                    bgcolor: isExpanded ? PRIMARY_LIGHT : 'transparent',
                    borderLeft: `3px solid ${isExpanded ? PRIMARY : 'transparent'}`,
                    transition: 'background 0.2s, color 0.2s, border-color 0.2s',
                    '&:hover': {
                      bgcolor: PRIMARY_LIGHT,
                      color: PRIMARY,
                    },
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown size={20} strokeWidth={2.5} color={PRIMARY} />
                  ) : (
                    <ChevronRight size={20} strokeWidth={2.5} color={TEXT_MUTED} />
                  )}
                  {section.label}
                </Box>
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <Box sx={{ pl: 3.5, pr: 1, py: 0.75, borderLeft: `2px solid ${BORDER}`, ml: 1.5 }}>
                        {section.children.map((child, i) => {
                          const hasSubMenu = child.children && child.children.length > 0;
                          const isSubExpanded = hasSubMenu && expandedSubIds.has(child.id);
                          const isSubActive = hasSubMenu && child.children!.some((s) => s.id === activeId);
                          const isParentActive = activeId === child.id || isSubActive;
                          return (
                            <motion.div
                              key={child.id}
                              initial={{ opacity: 0, x: -4 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.05 * i }}
                            >
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTopicClick(child.id, section.id);
                                }}
                                sx={{
                                  py: 1.1,
                                  px: 2,
                                  cursor: 'pointer',
                                  borderRadius: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  fontFamily: SUBMENU_FONT,
                                  fontSize: '0.925rem',
                                  fontWeight: isParentActive ? 700 : 500,
                                  letterSpacing: '0.015em',
                                  bgcolor: isParentActive ? PRIMARY_LIGHT : 'transparent',
                                  color: isParentActive ? PRIMARY : '#475569',
                                  borderLeft: isParentActive ? `3px solid ${PRIMARY}` : '3px solid transparent',
                                  transition: 'background 0.2s, color 0.2s',
                                  '&:hover': {
                                    bgcolor: isParentActive ? PRIMARY_LIGHT : 'rgba(37, 99, 235, 0.08)',
                                    color: isParentActive ? PRIMARY_HOVER : TEXT_DARK,
                                  },
                                }}
                              >
                                {hasSubMenu && (
                                  isSubExpanded ? (
                                    <ChevronDown size={16} strokeWidth={2.5} color={isParentActive ? PRIMARY : TEXT_MUTED} />
                                  ) : (
                                    <ChevronRight size={16} strokeWidth={2.5} color={TEXT_MUTED} />
                                  )
                                )}
                                {child.label}
                              </Box>
                              {hasSubMenu && (
                                <AnimatePresence initial={false}>
                                  {isSubExpanded && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.2 }}
                                      style={{ overflow: 'hidden' }}
                                    >
                                      <Box sx={{ pl: 2.5, pr: 1, py: 0.5, borderLeft: `1px solid ${BORDER}`, ml: 2 }}>
                                        {child.children!.map((sub, j) => {
                                          const isSubActive = activeId === sub.id;
                                          return (
                                            <Box
                                              key={sub.id}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleTopicClick(sub.id, section.id);
                                              }}
                                              sx={{
                                                py: 1,
                                                px: 1.5,
                                                cursor: 'pointer',
                                                borderRadius: '8px',
                                                fontFamily: SUBMENU_FONT,
                                                fontSize: '0.875rem',
                                                fontWeight: isSubActive ? 700 : 500,
                                                bgcolor: isSubActive ? PRIMARY_LIGHT : 'transparent',
                                                color: isSubActive ? PRIMARY : '#475569',
                                                borderLeft: isSubActive ? `3px solid ${PRIMARY}` : '3px solid transparent',
                                                transition: 'background 0.2s, color 0.2s',
                                                '&:hover': {
                                                  bgcolor: isSubActive ? PRIMARY_LIGHT : 'rgba(37, 99, 235, 0.06)',
                                                  color: isSubActive ? PRIMARY_HOVER : TEXT_DARK,
                                                },
                                              }}
                                            >
                                              {sub.label}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              )}
                            </motion.div>
                          );
                        })}
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.li>
            );
          })}
        </Box>
      </Box>

      {/* Main Content + Right TOC */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'row' }}>
        {/* Main Content - menu (above on mobile) + this content visible together */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 0, md: 'auto' },
            py: { xs: 2, md: 4 },
            px: { xs: 2, sm: 4 },
            maxWidth: 900,
            width: '100%',
          }}
        >
          {/* Mobile: label so users see menu above and content here */}
          <Typography
            component="h2"
            sx={{
              display: { xs: 'block', md: 'none' },
              fontFamily: MENU_SECTION_FONT,
              fontSize: '0.75rem',
              fontWeight: 700,
              color: TEXT_MUTED,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 1.5,
              pt: 0,
            }}
          >
            Lesson content
          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, mb: 3 }}>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                startIcon={<ChevronLeft size={18} />}
                onClick={() => navigate('/')}
                sx={{
                  bgcolor: PRIMARY,
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
                  '&:hover': { bgcolor: PRIMARY_HOVER },
                }}
              >
                Home
              </Button>
            </motion.div>
          </Box>

          {activeId && TOPIC_CONTENT_IDS.has(activeId) ? (
          activeId === 'variables-datatypes' ? (
            <VariablesDatatypesView />
          ) : activeId === 'conditions-loops' || activeId === 'conditional-statements' || activeId === 'python-loops' ? (
            <ConditionsLoopsView onNavigate={setActiveId} activeId={activeId} />
          ) : activeId === 'number' ? (
            <NumberTopicView onNavigate={setActiveId} />
          ) : activeId === 'string' ? (
            <StringTopicView onNavigate={setActiveId} />
          ) : activeId === 'tuple' ? (
            <TupleTopicView onNavigate={setActiveId} />
          ) : activeId === 'list' ? (
            <ListTopicView onNavigate={setActiveId} />
          ) : activeId === 'dictionary' ? (
            <DictionaryTopicView onNavigate={setActiveId} />
          ) : activeId === 'sets' ? (
            <SetsTopicView onNavigate={setActiveId} />
          ) : activeId === 'functions' ? (
            <FunctionsTopicView onNavigate={setActiveId} />
          ) : activeId === 'python-lambda' ? (
            <PythonLambdaTopicView onNavigate={setActiveId} />
          ) : activeId === 'file-handling' ? (
            <FileHandlingTopicView onNavigate={setActiveId} />
          ) : activeId === 'iterators' ? (
            <IteratorsTopicView onNavigate={setActiveId} />
          ) : activeId === 'generators' ? (
            <GeneratorsTopicView onNavigate={setActiveId} />
          ) : activeId === 'try-except-finally' ? (
            <TryExceptFinallyTopicView onNavigate={setActiveId} />
          ) : activeId === 'python-json' ? (
            <PythonJsonTopicView onNavigate={setActiveId} />
          ) : activeId === 'oop' ? (
            <OOPLandingView onNavigate={setActiveId} />
          ) : activeId && OOP_SUB_CONTENT[activeId] ? (
            <OOPSubTopicView topicId={activeId} onNavigate={setActiveId} />
          ) : (
            <DatatypeTopicView topicId={activeId} />
          )
        ) : (
          <SectionLandingView
            key={selectedSectionId}
            content={SECTION_LANDING[selectedSectionId] ?? SECTION_LANDING.fundamentals}
            onStartWithTopic={(topicId) => setActiveId(topicId)}
            progress={progress}
            activeId={activeId}
          />
        )}
        </Box>

        {/* Right-side "On this page" navigation */}
        <PageTocRight items={PAGE_TOC[activeId ?? ''] ?? []} />
      </Box>
    </Box>
  );
};

export default PythonCourse;
